import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import { Icons, PRODUCT_ICON_MAP, classifyTraveler, formatCurrency } from "./bookingConstants";
import styles from "./AddBooking.module.css";
import s from "./BookingDetailPricing.module.css";
/* Itinerary-tab styles + route renderer — the flight detail block inside the
   pricing card reuses the Itinerary tab's exact classes so the two screens
   can never drift apart. */
import itin from "../../screens/Booking/BookingDetail/ItineraryTab.module.css";
import FlightRoute from "./FlightRoute";
import { useGetAirports } from "../../api/geo/useAirports";
import {
  CancellationStatusPill, CancelledDirectionBlock,
} from "./CancellationPricing";
import cs from "./CancellationPricing.module.css";
import {
  FileText, TrendingUp as LcTrendingUp, XCircle, AlertTriangle,
  RefreshCw, Receipt, Users, CheckCircle2, Plane, Clock, Tag, Percent,
  Hotel, Bed, Globe, Calendar, Moon, Building2, Bus, Car, ArrowRight,
} from "lucide-react";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const TYPE_CLS = { ADT: s.typeADT, CHD: s.typeCHD, INF: s.typeINF, "—": s.typeUnknown };
const ICON_BG  = {
  Flight: s.groupIconFlight,
  Hotel: s.groupIconHotel,
  Transfer: s.groupIconTransfer,
  Insurance: s.groupIconInsurance,
};

const PAX_TYPE_MAP = { Adult: "ADT", Child: "CHD", Infant: "INF" };

const GENDER_MAP = {
  MR: "MR", MRS: "MRS", MS: "MS", MISS: "MISS",
  MSTR: "BOY", BOY: "BOY", GIRL: "GIRL",
};

function getGenderLabel(traveler, paxType) {
  if (traveler.title) {
    const key = traveler.title.toUpperCase();
    if (GENDER_MAP[key]) return GENDER_MAP[key];
  }
  if (traveler.gender) {
    const g = traveler.gender.toUpperCase();
    if (paxType === "CHD" || paxType === "INF") return g === "MALE" || g === "M" ? "BOY" : "GIRL";
    return g === "MALE" || g === "M" ? "MR" : "MRS";
  }
  return "—";
}

function formatDOB(dob) {
  if (!dob) return "—";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dob)) return dob;
  const parts = dob.split("-");
  if (parts.length === 3) {
    const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
    return `${parts[2]} ${months[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
  }
  return dob;
}

function formatDateShort(d) {
  if (!d) return "—";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return d;
}

/* ── Itinerary-tab date/time helpers (identical output to ItineraryTab) ──── */

const FLIGHT_TYPE_LABELS_IT = { OW: "One Way", RT: "Round Trip", MC: "Multi City", MF: "Multi City", MULTI: "Multi City" };
/* Case-insensitive label lookup — stored flightType varies ("RT", "Multi", "MC") */
const flightTypeLabelOf = (ft) => FLIGHT_TYPE_LABELS_IT[String(ft || "").toUpperCase()] || ft || "";

/* "Fri 14 Mar" — used under the big times in the segment rows */
const formatDayDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
};

/* "14 Mar 2025" — used in the Product Summary title */
const formatLongDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
};

const fmtLegTime = (t) => (t ? String(t).slice(0, 5) : "—");

const calcLegDuration = (depTime, arrTime) => {
  if (!depTime || !arrTime) return null;
  try {
    const [dh, dm] = String(depTime).split(":").map(Number);
    const [ah, am] = String(arrTime).split(":").map(Number);
    let diff = (ah * 60 + am) - (dh * 60 + dm);
    if (diff < 0) diff += 24 * 60; // next day arrival
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ""}`.trim() : `${m}m`;
  } catch { return null; }
};

/* Checked-baggage kg — supports the nested API shape (leg.baggage.checked.kg)
   and the flat form shape (leg.baggageCheckedKg) used by the pricing data. */
const legCheckedKg = (leg) =>
  leg.baggage?.checked?.kg || (leg.baggageChecked ? leg.baggageCheckedKg : null) || null;

function incrementTicket(base, offset) {
  if (!base || offset === 0) return base;
  const match = base.match(/^(.*?)(\d+)$/);
  if (!match) return base;
  const prefix = match[1];
  const numStr = match[2];
  const incremented = parseInt(numStr, 10) + offset;
  return prefix + String(incremented).padStart(numStr.length, "0");
}

const defaultPricingEntry = () => ({
  netPrice: 0, tax: 0, vatTax: 0, commission: 0, segFee: 0,
  clientPrice: 0, costPrice: 0, sellingPrice: 0, markup: 0,
  ticketNumber: "",
});

/* Normalize hotel rooms — supports new rooms[] array OR legacy numberOfRooms */
function getHotelRooms(product) {
  if (Array.isArray(product.rooms) && product.rooms.length > 0) return product.rooms;
  const n = Math.max(1, parseInt(product.numberOfRooms, 10) || 1);
  return Array.from({ length: n }, (_, i) => ({
    id: `legacy-${product.id}-${i}`,
    roomType: product.roomType,
    roomName: product.roomName,
    boardName: product.boardName,
    boardCode: product.boardCode,
    roomRate: product.roomRate,
    roomCharacteristic: product.roomCharacteristic,
    roomStatus: product.roomStatus,
    numberOfRooms: 1,
  }));
}

/* ─── Formatted Number Input (European: 1.234,56) ───────────────────────── */

const NumberInput = ({ value, onChange, placeholder = "0,00", className }) => {
  const [focused, setFocused] = useState(false);
  const [local, setLocal] = useState("");

  useEffect(() => {
    if (!focused) setLocal(value == null || value === "" ? "" : String(value));
  }, [value, focused]);

  const display = (() => {
    if (focused) return local;
    if (local === "" || isNaN(parseFloat(local))) return "";
    return parseFloat(local).toLocaleString("nl-BE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  })();

  const handleChange = (e) => {
    let v = e.target.value.replace(/[^0-9.,\-]/g, "");
    v = v.replace(/,/g, ".");
    const parts = v.split(".");
    if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
    setLocal(v);
    onChange(v);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      value={display}
      onFocus={(e) => {
        setFocused(true);
        const num = parseFloat(value);
        if (value == null || value === "" || num === 0) {
          setLocal("");
        } else {
          setLocal(String(value));
          setTimeout(() => e.target.select(), 0);
        }
      }}
      onBlur={() => {
        setFocused(false);
        const n = parseFloat(local);
        onChange(isNaN(n) ? "" : String(n));
      }}
      onChange={handleChange}
    />
  );
};

/* ─── Inline SVG Icons ───────────────────────────────────────────────────── */

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
);

const SSRIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
);

const BedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3"/><path d="M2 11v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-7"/><path d="M7 9h10v2H7z"/></svg>
);

const TrendUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
);

const TrendDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
);

/* ─── Copiable Text ────────────────────────────────────────────────────── */

const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
);

const CopyableText = ({ text, className }) => {
  const [copied, setCopied] = useState(false);
  if (!text || text === "—") return <span className={className}>{text || "—"}</span>;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <span className={`${s.copiable} ${className || ""}`} onClick={handleCopy} title="Click to copy">
      {text}
      <span className={s.copyBtn}>{copied ? <span className={s.copiedTick}>&#10003;</span> : <CopyIcon />}</span>
    </span>
  );
};

/* ─── Header Meta Columns — labeled key-facts laid out across the card header ──
   The reference's signature: Route · Travel Date · Travellers · Pricing Mode ·
   Supplier Ref as evenly-spaced label-above-value columns. Empty values are
   dropped so nothing renders blank. */
const MetaField = ({ label, value, mono }) => (
  <div className={s.metaField}>
    <span className={s.metaLabel}>{label}</span>
    <span className={`${s.metaValue} ${mono ? s.metaValueMono : ""}`}>
      {value === undefined || value === null || value === "" ? "—" : value}
    </span>
  </div>
);

const MetaColumns = ({ items }) => {
  const shown = (items || []).filter(
    (it) => it && (it.always || (it.value !== undefined && it.value !== null && it.value !== ""))
  );
  if (shown.length === 0) return null;
  return (
    <div className={s.metaColumns}>
      {shown.map((it, i) => (
        <MetaField key={i} label={it.label} value={it.value} mono={it.mono} />
      ))}
    </div>
  );
};

/* A full-width left-column section heading (PASSENGER PRICING, ROOM PRICING …). */
const BlockLabel = ({ icon, children }) => (
  <div className={s.sectionBlockLabel}>
    {icon}{children}
  </div>
);

/* Small plane glyph (points up; rotated to point along the route line). */
const PlaneGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" /></svg>
);

/* Airport route rendered as bold codes joined by light arrows (CGN → AYT → DUS). */
const RouteLine = ({ airports }) => (
  <span className={s.segRoute}>
    {(airports || []).flatMap((a, i) =>
      i === 0
        ? [<span key={`a${i}`} className={s.segRouteAirport}>{a}</span>]
        : [
            <span key={`x${i}`} className={s.segRouteArrow}>→</span>,
            <span key={`a${i}`} className={s.segRouteAirport}>{a}</span>,
          ]
    )}
  </span>
);

/* ─── Cancellation banner (full-width brand-orange strip at the top of a card) ── */
const CancellationBanner = ({ canc }) => {
  if (!canc) return null;
  const n = canc.cancelledTravelers || 0;
  const travLine = n > 0 ? `${n} traveller${n !== 1 ? "s" : ""} cancelled. Prices and totals updated.` : null;
  const text = canc.banner || travLine || "Cancellation active. Prices and totals updated.";
  const cancelledOn = canc.creditNote?.issuedOn;
  return (
    <div className={s.cancelBanner}>
      <span className={s.cancelBannerIcon}><AlertTriangle /></span>
      <div className={s.cancelBannerBody}>
        <span className={s.cancelBannerTitle}>Cancellation Active</span>
        <span className={s.cancelBannerText}>{text}</span>
      </div>
      {cancelledOn && (
        <div className={s.cancelBannerMeta}>
          <div className={s.cancelBannerMetaItem}>
            <span className={s.cancelBannerMetaLabel}>Credit Note Issued</span>
            <span className={s.cancelBannerMetaValue}>{cancelledOn}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── KPI tiles + traveller strip (full width, shown only when cancelled) ──── */
const KpiTile = ({ label, icon, amount, amountCls, sub, children }) => (
  <div className={s.kpiTile}>
    <div className={s.kpiTileLabel}>{icon}{label}</div>
    {children ? children : <div className={`${s.kpiTileAmount} ${amountCls || ""}`}>{amount}</div>}
    {sub && <div className={s.kpiTileSub}>{sub}</div>}
  </div>
);

const CancellationOverview = ({ canc }) => {
  if (!canc) return null;
  const cn = canc.creditNote;
  const hasTravelers = (canc.totalTravelers || 0) > 0;
  return (
    <>
      <div className={s.kpiGrid}>
        <KpiTile label="Original Price" icon={<FileText size={11} />} amount={formatCurrency(canc.originalSelling)} amountCls={s.kpiBlue} sub="Original booking value" />
        <KpiTile label="Active Amount" icon={<LcTrendingUp size={11} />} amount={formatCurrency(canc.activeSelling)} amountCls={s.kpiActive} sub="Remaining services" />
        <KpiTile label="Cancelled Amount" icon={<XCircle size={11} />} amount={formatCurrency(canc.cancelledValue)} amountCls={s.kpiCancelled} sub="Cancelled services" />
        <KpiTile label="Cancellation Cost" icon={<AlertTriangle size={11} />} amount={formatCurrency(canc.totalFees)} amountCls={s.kpiFees} sub="Supplier + agency" />
        <KpiTile label="Refund to Client" icon={<RefreshCw size={11} />} amount={formatCurrency(canc.refundDue)} amountCls={s.kpiRefund} sub="To refund to customer" />
        <KpiTile label="Credit Note" icon={<Receipt size={11} />} sub={cn?.number ? "Credit note issued" : "No credit note yet"}>
          <div className={s.kpiCnRow}>
            <span className={`${s.kpiCnNumber} ${cn?.number ? "" : s.kpiCnNumberPending}`}>{cn?.number || "To be issued"}</span>
            {cn?.number && <span className={s.kpiCnPill}>{cn.status || "Issued"}</span>}
          </div>
          <span className={s.kpiCnIcon}><FileText size={18} /></span>
        </KpiTile>
      </div>
      {hasTravelers && (
        <div className={s.travStrip}>
          <div className={s.travItem}>
            <span className={s.travIconBox}><Users size={14} /></span>
            <span className={s.travText}><span className={s.travItemLabel}>Total Travellers</span><span className={s.travItemValue}>{canc.totalTravelers}</span></span>
          </div>
          <div className={s.travItem}>
            <span className={`${s.travIconBox} ${s.travIconActive}`}><CheckCircle2 size={14} /></span>
            <span className={s.travText}><span className={s.travItemLabel}>Active Travellers</span><span className={`${s.travItemValue} ${s.travValueActive}`}>{canc.activeTravelers}</span></span>
          </div>
          <div className={s.travItem}>
            <span className={`${s.travIconBox} ${s.travIconCancelled}`}><XCircle size={14} /></span>
            <span className={s.travText}><span className={s.travItemLabel}>Cancelled Travellers</span><span className={`${s.travItemValue} ${s.travValueCancelled}`}>{canc.cancelledTravelers}</span></span>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── Sticky summary rail (right column) ─────────────────────────────────────
   Built from the same totals the tables compute — no new math. When the product
   is (partially) cancelled it surfaces the Original → Active story; otherwise it
   lists the per-line selling subtotals and the cost/VAT decomposition. */
const RailLine = ({ label, value, strong }) => (
  <div className={s.sideLine}>
    <span className={s.sideLineLabel}>{label}</span>
    <span className={s.sideLineVals}><span className={`${s.sideLineVal} ${strong ? s.sideLineActiveVal : ""}`}>{formatCurrency(value)}</span></span>
  </div>
);

const CancellationSummaryRail = ({ canc, onViewCreditNote }) => {
  if (!canc) return null;
  const cn = canc.creditNote;
  return (
    <div className={s.cancSum}>
      <div className={s.cancSumHead}><Receipt size={14} /> Cancellation Summary</div>
      <div className={s.cancSumBody}>
        <div className={s.cancSumRow}><span className={s.cancSumLabel}>Original Value</span><span className={s.cancSumVal}>{formatCurrency(canc.originalSelling)}</span></div>
        <div className={s.cancSumRow}><span className={s.cancSumLabel}>Cancelled Value</span><span className={`${s.cancSumVal} ${s.cancSumValCancelled}`}>{formatCurrency(canc.cancelledValue)}</span></div>
        <div className={s.cancSumRow}><span className={s.cancSumLabel}>Active Value</span><span className={`${s.cancSumVal} ${s.cancSumValActive}`}>{formatCurrency(canc.activeSelling)}</span></div>
        <div className={s.cancSumDivider} />
        <div className={s.cancSumRow}><span className={s.cancSumLabel}>Supplier Cancellation Fee</span><span className={s.cancSumVal}>{formatCurrency(canc.supplierFee)}</span></div>
        <div className={s.cancSumRow}><span className={s.cancSumLabel}>Sunsky Cancellation Fee</span><span className={s.cancSumVal}>{formatCurrency(canc.agencyFee)}</span></div>
        <div className={s.cancSumRow}><span className={s.cancSumLabel}>Total Cancellation Fees</span><span className={`${s.cancSumVal} ${s.cancSumValFees}`}>{formatCurrency(canc.totalFees)}</span></div>
        <div className={s.cancSumRefund}>
          <span className={s.cancSumRefundLabel}>Refund Due</span>
          <span className={s.cancSumRefundVal}>{formatCurrency(canc.refundDue)}</span>
        </div>
        {cn?.number && (
          <>
            <div className={s.cancSumCnRow}>
              <span className={s.cancSumLabel}>Credit Note</span>
              <span style={{ textAlign: "right" }}>
                <span className={s.cancSumCnNumber}>{cn.number}</span>
                {cn.issuedOn && <div className={s.cancSumCnDate}>Issued on {cn.issuedOn}</div>}
              </span>
            </div>
            <button type="button" className={s.viewCnBtnSide} onClick={onViewCreditNote}>
              <Receipt /> View Credit Note
            </button>
          </>
        )}
        {!cn?.number && (
          <div className={s.cancSumCnRow}>
            <span className={s.cancSumLabel}>Credit Note</span>
            <span className={s.cancSumCnNumber} style={{ color: "var(--accent-amber, #f5a51e)" }}>To be issued</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* The full right-hand rail: Pricing Summary + (Cancellation Summary) + Final price.
   `selling` = { title, lines:[{label,value}], subtotal:{label,value} } describing
   the active/current selling price. The per-line cost/VAT breakdown is intentionally
   omitted here — it already lives in the product table's own columns + totals row. */
const PricingRail = ({ selling, canc, finalLabel, finalValue, finalSub, onViewCreditNote }) => (
  <aside className={s.cardSide}>
    <div className={s.sideCard}>
      <div className={s.sideHead}>Pricing Summary</div>
      <div className={s.sideSection}>
        <div className={s.sideSectionTitle}>
          <span>{selling.title}</span>
        </div>
        {canc ? (
          <>
            <div className={s.sideLine}>
              <span className={s.sideLineLabel}>Original Value</span>
              <span className={s.sideLineVals}><span className={s.sideLineVal}>{formatCurrency(canc.originalSelling)}</span></span>
            </div>
            <div className={s.sideLine}>
              <span className={s.sideLineLabel}>Cancelled Value</span>
              <span className={s.sideLineVals}><span className={`${s.sideLineVal} ${s.sideLineCancelled}`}>− {formatCurrency(canc.cancelledValue)}</span></span>
            </div>
            <div className={s.sideSub}>
              <span className={s.sideSubLabel}>Active Total</span>
              <span className={s.sideSubVals}><span className={`${s.sideSubVal} ${s.sideLineActiveVal}`}>{formatCurrency(canc.activeSelling)}</span></span>
            </div>
          </>
        ) : (
          <>
            {selling.lines.map((ln, i) => (
              <RailLine key={i} label={ln.label} value={ln.value} />
            ))}
            <div className={s.sideSub}>
              <span className={s.sideSubLabel}>{selling.subtotal.label}</span>
              <span className={s.sideSubVals}><span className={s.sideSubVal}>{formatCurrency(selling.subtotal.value)}</span></span>
            </div>
          </>
        )}
      </div>
    </div>

    {canc && <CancellationSummaryRail canc={canc} onViewCreditNote={onViewCreditNote} />}

    <div className={`${s.finalBox} ${s.finalBoxAnchored}`}>
      <div className={s.finalLabel}>{finalLabel}</div>
      <div className={s.finalValue}>{formatCurrency(finalValue)}</div>
      {finalSub && <div className={s.finalSub}>{finalSub}</div>}
    </div>
  </aside>
);

/* Compact, full-width footer summary for the wide Flight card: labeled cost
   columns + a right-side Final Selling Price block, all in one cohesive panel
   (no oversized empty box). */
const FlightFooterSummary = ({ columns, finalLabel, finalValue, finalSub, canc, onViewCreditNote }) => {
  const cn = canc?.creditNote;
  return (
    <div className={s.flightFooter}>
      <div className={s.flightFooterBody}>
        <div className={s.flightFooterCols}>
          {columns.map((c, i) => (
            <div key={i} className={s.ffCol}>
              <span className={s.ffColLabel}>{c.label}</span>
              <span className={`${s.ffColValue} ${c.cls || ""}`}>{c.minus ? "− " : ""}{formatCurrency(c.value)}</span>
            </div>
          ))}
        </div>
        <div className={s.flightFooterFinal}>
          <span className={s.ffFinalLabel}>{finalLabel}</span>
          <span className={s.ffFinalValue}>{formatCurrency(finalValue)}</span>
          {finalSub && <span className={s.ffFinalSub}>{finalSub}</span>}
        </div>
      </div>
      {canc && (canc.refundDue || 0) > 0 && (
        <div className={s.flightFooterCn}>
          <span className={s.ffCnText}>
            {cn?.number
              ? <>Credit note <strong className={s.ffCnNum}>{cn.number}</strong>{cn.issuedOn ? ` · issued ${cn.issuedOn}` : ""} — refund of <strong>{formatCurrency(canc.refundDue)}</strong> due to the customer.</>
              : <>Credit note to be issued — refund of <strong>{formatCurrency(canc.refundDue)}</strong> due to the customer.</>}
          </span>
          {cn?.number && (
            <button type="button" className={s.ffCnBtn} onClick={onViewCreditNote}>
              <Receipt /> View Credit Note
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Flight Pricing Table ───────────────────────────────────────────────── */

const formatVatBadge = (meta) => {
  if (!meta?.vat_mode) return null;
  if (meta.vat_mode === "NO_VAT" || meta.vat_mode === "AGENT") return "No VAT";
  if (meta.vat_mode === "TOMS") return "TOMS";
  if (meta.vat_mode === "REVERSE_CHARGE") return "Rev. Charge";
  if (meta.vat_mode === "STANDARD") return `${meta.vat_rate}%`;
  return meta.vat_mode;
};

// VAT recalculates on every price keystroke. The cell keeps showing the last
// good amount + badge while a fresh request is in flight (just dimmed), so
// the table row never jumps and the badge doesn't flicker. Once the new
// result arrives the refs catch up and the value crossfades in.
const VATDisplay = ({ value, meta, loading }) => {
  const lastValueRef = useRef(value);
  const lastMetaRef = useRef(meta);
  if (!loading) {
    if (value != null) lastValueRef.current = value;
    if (meta) lastMetaRef.current = meta;
  }
  const dispValue = loading ? lastValueRef.current : value;
  const dispMeta = loading ? lastMetaRef.current : meta;
  const badgeText = formatVatBadge(dispMeta);

  return (
    <div className={s.vatDisplay}>
      <span className={`${s.vatValue} ${loading ? s.vatLoading : ""}`}>
        {formatCurrency(dispValue || 0)}
      </span>
      <span
        className={`${s.vatModeBadge} ${!badgeText ? s.vatModeBadgeEmpty : ""} ${loading ? s.vatModeBadgeLoading : ""}`}
        title={dispMeta?.reason || ""}
        aria-hidden={!badgeText}
      >
        {badgeText || "—"}
      </span>
    </div>
  );
};

const FlightPricingTable = ({ travelers, assignments, pricing, productId, onChange, onCopyToAll, onAutoFillTickets, directionLabel, vatMeta, vatLoading, travelerMods = [] }) => {
  const tis = [...new Set(
    assignments.filter((a) => a.productId === productId).map((a) => a.travelerIndex)
  )];

  const getKey = (ti) => directionLabel ? `${ti}-${productId}-${directionLabel}` : `${ti}-${productId}`;

  const cancelledTravelerIds = useMemo(() => {
    const set = new Set();
    travelerMods.forEach((m) => set.add(m.travelerId));
    return set;
  }, [travelerMods]);

  const modByTravelerId = useMemo(() => {
    const map = {};
    travelerMods.forEach((m) => { map[m.travelerId] = m; });
    return map;
  }, [travelerMods]);

  const isCancelled = (ti) => {
    const t = travelers[ti];
    if (!t) return false;
    if (t.status === "Cancelled") return true;
    if (t.id && cancelledTravelerIds.has(t.id)) return true;
    return false;
  };

  const handleCopyToAll = (sourceTi) => {
    if (!onCopyToAll || tis.length <= 1) return;
    const sourceKey = getKey(sourceTi);
    const activeTis = tis.filter((other) => other !== sourceTi && !isCancelled(other));
    if (activeTis.length === 0) return;
    const targetKeys = activeTis.map((other) => getKey(other));
    onCopyToAll(sourceKey, targetKeys, ["netPrice", "tax", "commission", "segFee"]);
  };

  const handleTicketChange = (ti, value) => {
    const idx = tis.indexOf(ti);
    if (idx === 0 && tis.length > 1) {
      onAutoFillTickets(productId, directionLabel, value, tis);
    } else {
      onChange(getKey(ti), "ticketNumber", value);
    }
  };

  const hasCancelledTravelers = tis.some((ti) => isCancelled(ti));
  const activeTis = tis.filter((ti) => !isCancelled(ti));
  const cancelledTis = tis.filter((ti) => isCancelled(ti));

  const sumTis = (list) => list.reduce((acc, ti) => {
    const p = pricing[getKey(ti)] || {};
    acc.netPrice    += p.netPrice    || 0;
    acc.tax         += p.tax         || 0;
    acc.vatTax      += p.vatTax      || 0;
    acc.commission  += p.commission  || 0;
    acc.segFee      += p.segFee      || 0;
    acc.clientPrice += p.clientPrice || 0;
    return acc;
  }, { netPrice: 0, tax: 0, vatTax: 0, commission: 0, segFee: 0, clientPrice: 0 });

  const totals = sumTis(tis);
  const activeTotals = hasCancelledTravelers ? sumTis(activeTis) : totals;

  if (tis.length === 0) return (
    <div className={s.emptyTableMsg}>
      <div className={s.emptyTableIcon}><InfoIcon /></div>
      <span>No travelers assigned to this product yet.</span>
    </div>
  );

  const renderRow = (ti, idx, cancelled) => {
    const t = travelers[ti];
    if (!t) return null;
    const key        = getKey(ti);
    const p          = pricing[key] || {};
    const paxCat     = PAX_TYPE_MAP[classifyTraveler(t.dob)] || "ADT";
    const genderLbl  = getGenderLabel(t, paxCat);
    const name       = [t.firstName, (t.lastName || "").toUpperCase()].filter(Boolean).join(" ") || `Traveler ${ti + 1}`;
    const dob        = formatDOB(t.dob);
    const sub        = [genderLbl !== "—" ? genderLbl : null, dob !== "—" ? dob : null].filter(Boolean).join(" · ");
    const mod        = cancelled && t.id ? modByTravelerId[t.id] : null;

    return (
      <Fragment key={ti}>
        <div className={`${s.flightRow} ${cancelled ? s.flightRowCancelled : ""}`}>
          <div className={s.fCell}>
            <span className={`${s.cellType} ${TYPE_CLS[paxCat] || s.typeUnknown}`}>{paxCat}</span>
          </div>
          <div className={`${s.fCell} ${s.fCellName}`}>
            <span className={s.travIndex}>{ti + 1}</span>
            <span className={s.travNameWrap}>
              <span className={s.travName} title={name}>
                {name}
                {cancelled && <span className={s.travCancelledBadge}>Cancelled</span>}
              </span>
              {sub && <span className={s.travSub}>{sub}</span>}
            </span>
          </div>
          <div className={s.fCell}>
            {cancelled
              ? <span className={s.cancelledReadonly}>{p.ticketNumber || "—"}</span>
              : <input className={`${s.fInput} ${s.ticketInput}`} type="text" placeholder={idx === 0 ? "Master ticket no." : "Auto-filled"} value={p.ticketNumber || ""} onChange={(e) => handleTicketChange(ti, e.target.value)} />
            }
          </div>
          <div className={s.fCell}>
            {cancelled
              ? <span className={s.cancelledStrike}>{formatCurrency(p.netPrice || 0)}</span>
              : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.netPrice ?? ""} onChange={(v) => onChange(key, "netPrice", v)} />
            }
          </div>
          <div className={s.fCell}>
            {cancelled
              ? <span className={s.cancelledStrike}>{formatCurrency(p.tax || 0)}</span>
              : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.tax ?? ""} onChange={(v) => onChange(key, "tax", v)} />
            }
          </div>
          <div className={s.fCell}>
            {cancelled
              ? <span className={s.cancelledStrike}>{formatCurrency(p.vatTax || 0)}</span>
              : <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
            }
          </div>
          <div className={s.fCell}>
            {cancelled
              ? <span className={s.cancelledStrike}>{formatCurrency(p.commission || 0)}</span>
              : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.commission ?? ""} onChange={(v) => onChange(key, "commission", v)} />
            }
          </div>
          <div className={s.fCell}>
            {cancelled
              ? <span className={s.cancelledStrike}>{formatCurrency(p.segFee || 0)}</span>
              : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.segFee ?? ""} onChange={(v) => onChange(key, "segFee", v)} />
            }
          </div>
          <div className={s.fCell}>
            <span className={`${s.clientPriceVal} ${cancelled ? s.cancelledStrike : ""}`}>{formatCurrency(p.clientPrice || 0)}</span>
          </div>
          <div className={s.fCell}>
            {!cancelled && tis.length > 1 && (
              <button type="button" className={s.copyToAllBtn} onClick={() => handleCopyToAll(ti)} title="Copy this traveler's prices to all others">{Icons.copy}</button>
            )}
          </div>
        </div>

        {cancelled && mod && (
          <div className={s.cancEffectRow}>
            <div className={s.cancEffectInner}>
              <span className={s.cancEffectItem}>
                <span className={s.cancEffectLabel}>Original Price</span>
                <span className={s.cancEffectVal}>{formatCurrency(p.clientPrice || 0)}</span>
              </span>
              <span className={s.cancEffectArrow}>&rarr;</span>
              <span className={s.cancEffectItem}>
                <span className={s.cancEffectLabel}>Supplier Cost</span>
                <span className={s.cancEffectValMuted}>{formatCurrency(mod.supplierCost)}</span>
              </span>
              <span className={s.cancEffectPlus}>+</span>
              <span className={s.cancEffectItem}>
                <span className={s.cancEffectLabel}>Sunsky Fee</span>
                <span className={s.cancEffectValMuted}>{formatCurrency(mod.agencyFee)}</span>
              </span>
              <span className={s.cancEffectEquals}>=</span>
              <span className={s.cancEffectItem}>
                <span className={s.cancEffectLabel}>Customer Refund</span>
                <span className={s.cancEffectValRefund}>{formatCurrency(mod.refundAmount)}</span>
              </span>
            </div>
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <div className={s.flightTable}>
      <div className={s.flightTableHead}>
        <span className={s.fth}>TYPE</span>
        <span className={s.fth}>TRAVELER</span>
        <span className={s.fth}>TICKET NUMBER</span>
        <span className={`${s.fth} ${s.fthRight}`}>NET PRICE</span>
        <span className={`${s.fth} ${s.fthRight}`}>TAX</span>
        <span className={`${s.fth} ${s.fthRight}`}>VAT TAX</span>
        <span className={`${s.fth} ${s.fthRight}`}>COMMISSION</span>
        <span className={`${s.fth} ${s.fthRight}`}>SEG. FEE</span>
        <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>CLIENT PRICE</span>
        <span className={s.fth} />
      </div>

      <div className={s.flightTableBody}>
        {activeTis.map((ti, idx) => renderRow(ti, idx, false))}
        {cancelledTis.length > 0 && (
          <>
            <div className={s.cancelledDivider}>
              <XCircle size={13} />
              <span>Cancelled Traveler{cancelledTis.length > 1 ? "s" : ""}</span>
            </div>
            {cancelledTis.map((ti, idx) => renderRow(ti, idx, true))}
          </>
        )}
      </div>

      {hasCancelledTravelers ? (
        <>
          <div className={`${s.flightTotalsRow} ${s.flightTotalsRowCancelled}`}>
            <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>
              CANCELLED
            </span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.netPrice - activeTotals.netPrice)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.tax - activeTotals.tax)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.vatTax - activeTotals.vatTax)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.commission - activeTotals.commission)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.segFee - activeTotals.segFee)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.clientPrice - activeTotals.clientPrice)}</span>
            <span />
          </div>
          <div className={s.flightTotalsRow}>
            <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>
              {directionLabel ? `${directionLabel.toUpperCase()} ACTIVE` : "ACTIVE TOTAL"}
            </span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.netPrice)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.tax)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.vatTax)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.commission)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.segFee)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(activeTotals.clientPrice)}</span>
            <span />
          </div>
        </>
      ) : (
        <div className={s.flightTotalsRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>
            {directionLabel ? `${directionLabel.toUpperCase()} TOTAL` : "TOTAL"}
          </span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.netPrice)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.tax)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.vatTax)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.commission)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.segFee)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(totals.clientPrice)}</span>
          <span />
        </div>
      )}
    </div>
  );
};

/* ─── Generic Pricing Table (Transfer / Insurance / etc.) ───────────────── */

const PricingTable = ({ travelers, assignments, pricing, productId, onChange, vatMeta, vatLoading, travelerMods = [] }) => {
  const tis = [...new Set(assignments.filter((a) => a.productId === productId).map((a) => a.travelerIndex))];
  const getKey = (ti) => `${ti}-${productId}`;

  const cancelledTravelerIds = useMemo(() => {
    const set = new Set();
    travelerMods.forEach((m) => set.add(m.travelerId));
    return set;
  }, [travelerMods]);

  const isCancelled = (ti) => {
    const t = travelers[ti];
    if (!t) return false;
    return t.status === "Cancelled" || (t.id && cancelledTravelerIds.has(t.id));
  };

  const activeTis = tis.filter((ti) => !isCancelled(ti));
  const cancelledTis = tis.filter((ti) => isCancelled(ti));
  const hasCancelledTravelers = cancelledTis.length > 0;

  const sumTis = (list) => list.reduce((acc, ti) => {
    const p = pricing[getKey(ti)] || {};
    acc.cost    += p.costPrice    || 0;
    acc.selling += p.sellingPrice || 0;
    acc.vatTax  += p.vatTax       || 0;
    return acc;
  }, { cost: 0, selling: 0, vatTax: 0 });

  const subtotals = sumTis(tis);
  subtotals.markup = subtotals.selling - subtotals.cost;
  const activeSubtotals = hasCancelledTravelers ? sumTis(activeTis) : subtotals;
  activeSubtotals.markup = activeSubtotals.selling - activeSubtotals.cost;

  if (tis.length === 0) {
    const singleKey = `single-${productId}`;
    const sp = pricing[singleKey] || { costPrice: 0, markup: 0, vatTax: 0, sellingPrice: 0 };
    return (
      <div className={s.table}>
        <div className={s.tableHead}>
          <span className={s.fth} style={{ gridColumn: "1 / 4" }}>Product</span>
          <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
          <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
          <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
          <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
        </div>
        <div className={s.tableBody}>
          <div className={s.row}>
            <div className={`${s.fCell} ${s.fCellName}`} style={{ gridColumn: "1 / 4" }}>
              <span className={s.travName}>Whole product price</span>
            </div>
            <div className={s.fCell}>
              <NumberInput className={`${s.fInput} ${s.numInput}`} value={sp.costPrice ?? ""} onChange={(v) => onChange(singleKey, "costPrice", v)} />
            </div>
            <div className={s.fCell}>
              <NumberInput className={`${s.fInput} ${s.numInput}`} value={sp.markup ?? ""} onChange={(v) => onChange(singleKey, "markup", v)} />
            </div>
            <div className={s.fCell}>
              <VATDisplay value={sp.vatTax} meta={vatMeta?.[singleKey]} loading={vatLoading?.[singleKey]} />
            </div>
            <div className={s.fCell}>
              <span className={s.clientPriceVal}>{formatCurrency(sp.sellingPrice || 0)}</span>
            </div>
          </div>
        </div>
        <div className={s.subtotalRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>Subtotal</span>
          <span className={s.flightTotalVal}>{formatCurrency(sp.costPrice || 0)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(sp.markup || 0)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(sp.vatTax || 0)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(sp.sellingPrice || 0)}</span>
        </div>
      </div>
    );
  }

  const renderRow = (ti, cancelled) => {
    const t = travelers[ti];
    if (!t) return null;
    const key    = getKey(ti);
    const p      = pricing[key] || { costPrice: 0, sellingPrice: 0, markup: 0, vatTax: 0 };
    const name   = [t.firstName, (t.lastName || "").toUpperCase()].filter(Boolean).join(" ") || `Traveler ${ti + 1}`;
    const type   = classifyTraveler(t.dob);
    return (
      <div key={ti} className={`${s.row} ${cancelled ? s.flightRowCancelled : ""}`}>
        <div className={`${s.fCell} ${s.fCellName}`}>
          <span className={s.cellNameIndex}>{ti + 1}</span>
          <span className={s.travName}>
            {name}
            {cancelled && <span className={s.travCancelledBadge}>Cancelled</span>}
          </span>
        </div>
        <div className={s.fCell}>
          <span className={`${s.cellType} ${TYPE_CLS[type] || s.typeUnknown}`}>{type}</span>
        </div>
        <div className={s.fCell}>
          <span className={s.cellGender}>{t.gender || "—"}</span>
        </div>
        <div className={s.fCell}>
          {cancelled
            ? <span className={s.cancelledStrike}>{formatCurrency(p.costPrice || 0)}</span>
            : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.costPrice ?? ""} onChange={(v) => onChange(key, "costPrice", v)} />
          }
        </div>
        <div className={s.fCell}>
          {cancelled
            ? <span className={s.cancelledStrike}>{formatCurrency(p.markup || 0)}</span>
            : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
          }
        </div>
        <div className={s.fCell}>
          {cancelled
            ? <span className={s.cancelledStrike}>{formatCurrency(p.vatTax || 0)}</span>
            : <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
          }
        </div>
        <div className={s.fCell}>
          <span className={`${s.clientPriceVal} ${cancelled ? s.cancelledStrike : ""}`}>{formatCurrency(p.sellingPrice)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={s.table}>
      <div className={s.tableHead}>
        <span className={s.fth}>Traveler</span>
        <span className={s.fth}>Type</span>
        <span className={s.fth}>Gender</span>
        <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
        <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
        <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
        <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
      </div>
      <div className={s.tableBody}>
        {activeTis.map((ti) => renderRow(ti, false))}
        {cancelledTis.length > 0 && (
          <>
            <div className={s.cancelledDivider}>
              <XCircle size={13} />
              <span>Cancelled Traveler{cancelledTis.length > 1 ? "s" : ""}</span>
            </div>
            {cancelledTis.map((ti) => renderRow(ti, true))}
          </>
        )}
      </div>
      {hasCancelledTravelers ? (
        <>
          <div className={`${s.subtotalRow} ${s.flightTotalsRowCancelled}`}>
            <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>Cancelled</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(subtotals.cost - activeSubtotals.cost)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(subtotals.markup - activeSubtotals.markup)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(subtotals.vatTax - activeSubtotals.vatTax)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(subtotals.selling - activeSubtotals.selling)}</span>
          </div>
          <div className={s.subtotalRow}>
            <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>Active Total</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeSubtotals.cost)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeSubtotals.markup)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeSubtotals.vatTax)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(activeSubtotals.selling)}</span>
          </div>
        </>
      ) : (
        <div className={s.subtotalRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 4" }}>Subtotal</span>
          <span className={s.flightTotalVal}>{formatCurrency(subtotals.cost)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(subtotals.markup)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(subtotals.vatTax)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(subtotals.selling)}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Hotel Pricing Group (Per-Room, reads product.rooms[]) ─────────────── */

const HotelPricingGroup = ({ product, travelers, assignments = [], pricing, onChange, onResetPricing, accommodationIndex, vatMeta, vatLoading, canc, onSavePricing, savingPricing }) => {
  const rooms = getHotelRooms(product);

  const getRoomKey = (room) => `hotelroom-${product.id}-${room.id}`;

  const totals = rooms.reduce((acc, room) => {
    const p = pricing[getRoomKey(room)] || {};
    acc.net     += p.netPrice    || 0;
    acc.markup  += p.markup      || 0;
    acc.vat     += p.vatTax      || 0;
    acc.selling += p.sellingPrice|| 0;
    return acc;
  }, { net: 0, markup: 0, vat: 0, selling: 0 });

  // Nights — prefer the stored value (matches the Itinerary tab), else derive.
  const nights = product.nights || (() => {
    if (!product.checkIn || !product.checkOut) return null;
    const d = Math.round((new Date(product.checkOut) - new Date(product.checkIn)) / 86400000);
    return d > 0 ? d : null;
  })();

  // Room count + multi-room flag — identical logic to the Itinerary hotel card.
  const roomCount = Math.max(
    Number(product.numberOfRooms) || 0,
    rooms.reduce((sum, r) => sum + (Number(r.numberOfRooms) || 1), 0)
  ) || 1;
  const hasMultipleRooms = rooms.length > 1;
  const room0 = rooms[0] || {};

  // Travelers assigned to this hotel (same source the Itinerary tab counts).
  const travCount = assignments.filter((a) => a.productId === product.id).length;

  // Itinerary-style header derivations
  const stars = product.hotelStars ? ` ${"★".repeat(Math.min(5, Math.round(product.hotelStars)))}` : "";
  const hotelTitle = `${product.hotelName || "Hotel"}${stars}`;
  const hotelSub = product.checkIn && product.checkOut
    ? `${formatLongDate(product.checkIn)} → ${formatLongDate(product.checkOut)}${nights ? ` (${nights} night${nights !== 1 ? "s" : ""})` : ""}`
    : [product.hotelCity, product.hotelCountry].filter(Boolean).join(", ");
  const cityCountry = [product.hotelCity, product.hotelCountry].filter(Boolean).join(", ") || product.hotelAddress || "—";
  const headerStatus = canc ? (canc.cancelledAll ? "Cancelled" : "Partially Cancelled") : (product.status || "Pending");

  return (
    <div className={`${s.groupCard} ${itin.uCardHotel}`}>
      {/* ── Header — identical structure to the Itinerary tab hotel card ── */}
      <div className={itin.uHead}>
        <div className={itin.uHeadL}>
          <div className={`${itin.uIcon} ${itin.uIconHotel}`}><Hotel size={20} /></div>
          <span className={itin.uType}>HOTEL</span>
          <ItStatusPill status={headerStatus} />
        </div>
        <div className={itin.uHeadR}>
          {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
        </div>
      </div>

      <ItSummaryBar
        title={hotelTitle}
        subtitle={hotelSub}
        supplier={product.supplier}
        supplierRef={product.supplierRef}
      />

      <ItStatStrip items={[
        { icon: <Users size={16} />, label: "Travelers", value: `${travCount} traveler${travCount !== 1 ? "s" : ""}` },
        { icon: <Bed size={16} />, label: "Rooms", value: `${roomCount} room${roomCount !== 1 ? "s" : ""}` },
        { icon: <Tag size={16} />, label: "Board", value: hasMultipleRooms ? "See rooms below" : (product.boardName || product.boardCode || room0.boardName || room0.boardCode || "—") },
        { icon: <Bed size={16} />, label: "Room Type", value: hasMultipleRooms ? "Multiple rooms" : (room0.roomName || room0.roomType || "—") },
        { icon: <Globe size={16} />, label: "Room View", value: hasMultipleRooms ? "See rooms below" : (room0.roomCharacteristic || "—") },
        { icon: <Percent size={16} />, label: "VAT Role", value: product.supplierCommercialRole || "Merchant" },
      ]} />

      {/* Check-in / Check-out box */}
      <div className={itin.uBox}>
        <div className={itin.uBoxGrid}>
          <div className={itin.uBoxCell}>
            <span className={itin.uStatIcon}><Calendar size={16} /></span>
            <span className={itin.uStatTxt}>
              <span className={itin.uStatLabel}>Check-in</span>
              <span className={itin.uStatValue}>{formatLongDate(product.checkIn) || "—"}</span>
            </span>
          </div>
          <div className={itin.uBoxCell}>
            <span className={itin.uStatIcon}><Calendar size={16} /></span>
            <span className={itin.uStatTxt}>
              <span className={itin.uStatLabel}>Check-out</span>
              <span className={itin.uStatValue}>{formatLongDate(product.checkOut) || "—"}</span>
            </span>
          </div>
          <div className={itin.uBoxCell}>
            <span className={itin.uStatIcon}><Moon size={16} /></span>
            <span className={itin.uStatTxt}>
              <span className={itin.uStatLabel}>Nights</span>
              <span className={itin.uStatValue}>{nights ? `${nights} night${nights !== 1 ? "s" : ""}` : "—"}</span>
            </span>
          </div>
          <div className={itin.uBoxCell}>
            <span className={itin.uStatIcon}><Building2 size={16} /></span>
            <span className={itin.uStatTxt}>
              <span className={itin.uStatLabel}>City / Country</span>
              <span className={itin.uStatValue}>{cityCountry}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Rooms breakdown — every room listed when more than one (like Itinerary) */}
      {!canc && hasMultipleRooms && (
        <ItPartsBreakdown
          title={`Rooms (${rooms.length})`}
          items={rooms.map((r, i) => ({
            label: `${r.roomName || r.roomType || `Room ${i + 1}`}${(Number(r.numberOfRooms) || 1) > 1 ? ` ×${r.numberOfRooms}` : ""}`,
            sub: [r.boardName || r.boardCode, r.roomCharacteristic].filter(Boolean).join(" · "),
            status: (r.status || r.roomStatus) === "Cancelled" ? "Cancelled" : "Active",
          }))}
        />
      )}

      <CancellationBanner canc={canc} />
      <CancellationOverview canc={canc} />

      <div className={`${s.cardBody} ${s.cardBodyStacked}`}>
        <div className={s.cardMain}>
          <BlockLabel icon={<BedIcon />}>Room Pricing</BlockLabel>
          <div className={s.hotelInfoNote}>
            <InfoIcon />
            <span>Room price is not per traveler — it is per room.</span>
          </div>

          {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
          <div className={`${s.hotelTable} ${canc?.cancelledAll ? cs.cancelledTableWrap : ""}`}>
            <div className={s.hotelTableHead}>
              <span className={s.fth}>Room</span>
              <span className={s.fth}>Description</span>
              <span className={`${s.fth} ${s.fthRight}`}>Room Net Price</span>
              <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
              <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
              <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
            </div>

            <div className={s.hotelTableBody}>
              {rooms.map((room, idx) => {
                const key = getRoomKey(room);
                const p   = pricing[key] || { netPrice: 0, markup: 0, vatTax: 0, sellingPrice: 0 };

                const primaryParts = [room.roomType, room.boardName].filter(Boolean);
                const subParts     = [room.roomName, room.roomCharacteristic, room.roomRate].filter(Boolean);

                return (
                  <div key={room.id || idx} className={s.hotelRow}>
                    <div className={s.fCell}>
                      <span className={s.roomBadge}>Room {idx + 1}</span>
                    </div>
                    <div className={`${s.fCell} ${s.hotelDescCell}`}>
                      <span className={s.roomDescPrimary}>
                        {primaryParts.length > 0 ? primaryParts.join(" · ") : "Room"}
                      </span>
                      {subParts.length > 0 && (
                        <span className={s.roomDescSub}>{subParts.join(" · ")}</span>
                      )}
                    </div>
                    <div className={s.fCell}>
                      <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.netPrice ?? ""} onChange={(v) => onChange(key, "netPrice", v)} />
                    </div>
                    <div className={s.fCell}>
                      <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
                    </div>
                    <div className={s.fCell}>
                      <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
                    </div>
                    <div className={s.fCell}>
                      <span className={s.clientPriceVal}>{formatCurrency(p.sellingPrice || 0)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={s.hotelTotalsRow}>
              <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 3" }}>Accommodation Total</span>
              <span className={s.flightTotalVal}>{formatCurrency(totals.net)}</span>
              <span className={s.flightTotalVal}>{formatCurrency(totals.markup)}</span>
              <span className={s.flightTotalVal}>{formatCurrency(totals.vat)}</span>
              <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(totals.selling)}</span>
            </div>
          </div>
        </div>

        <div className={s.flightFooterWrap}>
          <FlightFooterSummary
            columns={canc
              ? [
                  { label: "Original Price", value: canc.originalSelling },
                  { label: "Cancelled Amount", value: canc.cancelledValue, cls: s.ffNeg, minus: true },
                  { label: "Cancellation Cost", value: canc.totalFees, cls: s.ffFees },
                  { label: "Active Amount", value: canc.activeSelling, cls: s.ffPos },
                  { label: "Refund to Client", value: canc.refundDue, cls: s.ffRefund },
                ]
              : [
                  { label: "Room Net", value: totals.net },
                  { label: "Markup", value: totals.markup },
                  { label: "VAT (incl.)", value: totals.vat },
                ]}
            canc={canc}
            finalLabel="Final Selling Price (Active)"
            finalValue={canc ? canc.activeSelling + (canc.totalFees || 0) : totals.selling}
            finalSub="incl. all taxes and fees"
          />
        </div>
      </div>
      <PricingSaveFooter onSave={onSavePricing} saving={savingPricing} />
    </div>
  );
};

/* ─── Transfer Pricing Group (Fixed price per product) ──────────────────── */

const TransferPricingGroup = ({ product, assignments = [], pricing, onChange, onResetPricing, vatMeta, vatLoading, canc, onSavePricing, savingPricing }) => {
  const key = `transfer-${product.id}`;
  const p = pricing[key] || { costPrice: 0, markup: 0, vatTax: 0, sellingPrice: 0 };

  const isRoundTrip = product.transferMoment === "RoundTrip";
  const arrLeg = product.arrivalLeg || {};
  const depLeg = product.departureLeg || {};

  const momentLabel = product.transferMoment === "RoundTrip" ? "Round Trip"
    : product.transferMoment === "Arrival" ? "Arrival Transfer"
    : product.transferMoment === "Departure" ? "Departure Transfer"
    : product.transferMoment === "Custom" ? "Custom Transfer"
    : product.transferMoment || "";

  // Primary leg drives the header title/date/time (arrival leg for round trips).
  const primaryLeg = isRoundTrip ? arrLeg : product;
  const route = isRoundTrip ? null : [product.pickupLocation, product.dropoffLocation].filter(Boolean).join(" → ");
  const routeTitle = primaryLeg.pickupLocation && primaryLeg.dropoffLocation
    ? `${primaryLeg.pickupLocation} → ${primaryLeg.dropoffLocation}`
    : "Transfer";
  const hdrSub = [product.transferMoment, product.transferType].filter(Boolean).length
    ? `${[momentLabel, product.transferType].filter(Boolean).join(" · ")} Transfer`
    : "";

  // Travelers — the same assignment-based count the Itinerary tab shows; fall
  // back to the stored passenger counts when no per-traveler assignment exists.
  const assignCount = assignments.filter((a) => a.productId === product.id).length;
  const paxCount = (Number(product.numberOfAdults) || 0) + (Number(product.numberOfChildren) || 0);
  const travCount = assignCount || paxCount;

  const headerStatus = canc ? (canc.cancelledAll ? "Cancelled" : "Partially Cancelled") : (product.status || "Pending");

  const fmtT = (t) => (t ? String(t).slice(0, 5) : "—");
  const pickTimeLabel = (leg) => leg.pickupTimeType === "tba" ? "To Be Advised"
    : leg.pickupTimeType === "voucher" ? "Follow Voucher" : fmtT(leg.pickupTime);
  const legFlightLabel = (leg) => {
    const code = [leg.flightAirlineCode, leg.flightFlightNumber].filter(Boolean).join("");
    return code ? `Flight ${code}${leg.flightTime ? ` · ${fmtT(leg.flightTime)}` : ""}` : null;
  };

  // From → To route box(es): two for round trips (arrival + departure), one otherwise.
  const routeLegs = isRoundTrip ? [arrLeg, depLeg] : [product];

  return (
    <div className={`${s.groupCard} ${itin.uCardTransfer}`}>
      {/* ── Header — identical structure to the Itinerary tab transfer card ── */}
      <div className={itin.uHead}>
        <div className={itin.uHeadL}>
          <div className={`${itin.uIcon} ${itin.uIconTransfer}`}><Bus size={20} /></div>
          <span className={itin.uType}>TRANSFER</span>
          <ItStatusPill status={headerStatus} />
        </div>
        <div className={itin.uHeadR}>
          {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
        </div>
      </div>

      <ItSummaryBar
        title={routeTitle}
        subtitle={hdrSub}
        supplier={product.supplier}
        supplierRef={product.supplierRef}
      />

      <ItStatStrip items={[
        { icon: <Users size={16} />, label: "Travelers", value: `${travCount} traveler${travCount !== 1 ? "s" : ""}` },
        { icon: <Tag size={16} />, label: "Transfer Type", value: product.transferType || "—" },
        { icon: <Calendar size={16} />, label: "Date", value: formatLongDate(primaryLeg.pickupDate) || "—" },
        { icon: <Clock size={16} />, label: "Time", value: fmtT(primaryLeg.pickupTime) },
        { icon: <Car size={16} />, label: "Vehicle Type", value: product.vehicleType ? `Private ${product.vehicleType}` : "—" },
        { icon: <Percent size={16} />, label: "VAT Role", value: product.supplierCommercialRole || "Agent" },
      ]} />

      {/* From → To route box(es) — no instructions/contact block (per request) */}
      {routeLegs.map((leg, i) => (
        <div key={i} className={itin.uBox}>
          <div className={itin.uRoute}>
            <div className={itin.uRouteEnd}>
              <span className={itin.uRouteTag}>From</span>
              <div className={itin.uRouteName}>{leg.pickupLocation || "—"}</div>
              <div className={itin.uRouteSub}>{formatLongDate(leg.pickupDate) || "—"} · {pickTimeLabel(leg)}</div>
            </div>
            <div className={itin.uRouteLine}>
              <span className={itin.uRouteDot} />
              <span className={itin.uRouteDash} />
              <span className={itin.uRouteArrow}><ArrowRight size={16} /></span>
              <span className={itin.uRouteDash} />
              <span className={itin.uRouteDot} />
            </div>
            <div className={`${itin.uRouteEnd} ${itin.uRouteEndRight}`}>
              <span className={itin.uRouteTag}>To</span>
              <div className={itin.uRouteName}>{leg.dropoffLocation || "—"}</div>
              {legFlightLabel(leg) ? <div className={itin.uRouteSub}>{legFlightLabel(leg)}</div> : null}
            </div>
          </div>
        </div>
      ))}

      <CancellationBanner canc={canc} />
      <CancellationOverview canc={canc} />

      <div className={`${s.cardBody} ${s.cardBodyStacked}`}>
        <div className={s.cardMain}>
          <BlockLabel>Transfer Pricing</BlockLabel>
          <div className={s.hotelInfoNote}>
            <InfoIcon />
            <span>Transfer price is fixed per product — not per traveler.</span>
          </div>

          {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
          <div className={`${s.hotelTable} ${canc?.cancelledAll ? cs.cancelledTableWrap : ""}`}>
            <div className={s.hotelTableHead}>
              <span className={s.fth}>Type</span>
              <span className={s.fth}>Description</span>
              <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
              <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
              <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
              <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
            </div>

            <div className={s.hotelTableBody}>
              <div className={s.hotelRow}>
                <div className={s.fCell}>
                  <span className={s.roomBadge}>{product.transferType || "Transfer"}</span>
                </div>
                <div className={`${s.fCell} ${s.hotelDescCell}`}>
                  <span className={s.roomDescPrimary}>{isRoundTrip ? "Round Trip" : (route || "—")}</span>
                  {product.vehicleType && <span className={s.roomDescSub}>Private {product.vehicleType}</span>}
                </div>
                <div className={s.fCell}>
                  <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.costPrice ?? ""} onChange={(v) => onChange(key, "costPrice", v)} />
                </div>
                <div className={s.fCell}>
                  <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
                </div>
                <div className={s.fCell}>
                  <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
                </div>
                <div className={s.fCell}>
                  <span className={s.clientPriceVal}>{formatCurrency(p.sellingPrice || 0)}</span>
                </div>
              </div>
            </div>

            <div className={s.hotelTotalsRow}>
              <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 3" }}>Transfer Total</span>
              <span className={s.flightTotalVal}>{formatCurrency(p.costPrice || 0)}</span>
              <span className={s.flightTotalVal}>{formatCurrency(p.markup || 0)}</span>
              <span className={s.flightTotalVal}>{formatCurrency(p.vatTax || 0)}</span>
              <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(p.sellingPrice || 0)}</span>
            </div>
          </div>
        </div>

        <div className={s.flightFooterWrap}>
          <FlightFooterSummary
            columns={canc
              ? [
                  { label: "Original Price", value: canc.originalSelling },
                  { label: "Cancelled Amount", value: canc.cancelledValue, cls: s.ffNeg, minus: true },
                  { label: "Cancellation Cost", value: canc.totalFees, cls: s.ffFees },
                  { label: "Active Amount", value: canc.activeSelling, cls: s.ffPos },
                  { label: "Refund to Client", value: canc.refundDue, cls: s.ffRefund },
                ]
              : [
                  { label: "Cost Price", value: p.costPrice || 0 },
                  { label: "Markup", value: p.markup || 0 },
                  { label: "VAT (incl.)", value: p.vatTax || 0 },
                ]}
            canc={canc}
            finalLabel="Final Selling Price (Active)"
            finalValue={canc ? canc.activeSelling + (canc.totalFees || 0) : (p.sellingPrice || 0)}
            finalSub="incl. all taxes and fees"
          />
        </div>
      </div>
      <PricingSaveFooter onSave={onSavePricing} saving={savingPricing} />
    </div>
  );
};

/* ─── Flight Pricing Group ───────────────────────────────────────────────── */

const ResetPriceIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
);

const SaveIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
);

/* Per-card Save Pricing footer — repeated at the bottom of every product
   pricing card so prices can be saved in place without scrolling up. Renders
   nothing when no save handler is supplied (e.g. the booking is locked). */
const PricingSaveFooter = ({ onSave, saving }) => {
  if (!onSave) return null;
  return (
    <div className={s.cardSaveFooter}>
      <button type="button" className={s.cardSaveBtn} onClick={onSave} disabled={saving}>
        {saving
          ? <><span className={s.cardSaveSpinner} aria-hidden="true" /> Saving…</>
          : <><SaveIcon /> Save Pricing</>}
      </button>
    </div>
  );
};

/* ── Itinerary-style status pill (identical classes to the Itinerary tab) ── */
const IT_STATUS_CLASS_KEY = {
  Confirmed: "Confirmed", Pending: "Pending", Cancelled: "Cancelled",
  Active: "Active", "Partially Cancelled": "Partial",
};
const ItStatusPill = ({ status }) => {
  const label = status || "Pending";
  const key = IT_STATUS_CLASS_KEY[label] || "Pending";
  return (
    <span className={`${itin.uStatus} ${itin[`uStatus${key}`]}`}>
      <span className={itin.uStatusDot} />
      {label}
    </span>
  );
};

/* ── Itinerary-style header block: 3-column summary bar + icon-stat strip.
   Reuses the Itinerary tab's exact classes so the pricing card header is
   pixel-identical to the itinerary card for every product type. ── */
const ItSummaryBar = ({ title, subtitle, supplier, supplierRef }) => (
  <div className={itin.uSummary}>
    <div className={itin.uSumCell}>
      <span className={itin.uSumLabel}>Product Summary</span>
      <span className={itin.uSumTitle}>{title || "—"}</span>
      {subtitle ? <span className={itin.uSumSub}>{subtitle}</span> : null}
    </div>
    <div className={itin.uSumCell}>
      <span className={itin.uSumLabel}>Supplier</span>
      <span className={itin.uSumTitle}>{supplier || "—"}</span>
    </div>
    <div className={itin.uSumCell}>
      <span className={itin.uSumLabel}>Supplier Ref</span>
      <span className={itin.uSumTitle}>{supplierRef ? <CopyableText text={supplierRef} /> : "—"}</span>
    </div>
  </div>
);

const ItStatStrip = ({ items }) => (
  <div className={itin.uStats}>
    {items.filter(Boolean).map((st, i) => (
      <div key={i} className={itin.uStat}>
        <span className={itin.uStatIcon}>{st.icon}</span>
        <span className={itin.uStatTxt}>
          <span className={itin.uStatLabel}>{st.label}</span>
          <span className={itin.uStatValue} title={typeof st.value === "string" ? st.value : undefined}>{st.value || "—"}</span>
        </span>
      </div>
    ))}
  </div>
);

/* Itinerary-style product breakdown (rooms / legs) — same classes as the
   Itinerary tab's PartsBreakdown. Renders nothing when there is nothing to list. */
const ItPartsBreakdown = ({ title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={itin.uPartsBox}>
      <div className={itin.uPartsHead}>{title}</div>
      <div className={itin.uPartsList}>
        {items.map((it, i) => (
          <div key={i} className={itin.uPartsRow}>
            <span className={itin.uPartsName}>
              {it.label}
              {it.sub ? <span className={itin.uPartsSub}>{it.sub}</span> : null}
            </span>
            <span className={`${itin.uPartsStatus} ${it.status === "Cancelled" ? itin.uPartsCancelled : itin.uPartsActive}`}>
              <span className={itin.uPartsDot} />{it.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Itinerary-style flight segments — the Itinerary tab's exact visual system.
   Each leg renders as a full route row: direction tag + PNR / Fare / Baggage /
   status chips, big times, IATA badges, airport names, dates, and the dashed
   line-with-plane track with duration + Direct/stops. Handles One Way, Round
   Trip and Multi-City; `forcedDirection` renders the per-direction blocks of
   SPLIT-priced flights with the correct Outbound / Inbound tags. ── */
const FlightSegmentsItinerary = ({ legs, flightType, product, airportMap = {}, forcedDirection = null, cancelledAll = false, cancelledDirections = [] }) => {
  if (!legs || legs.length === 0) {
    return (
      <div className={itin.flightSegments}>
        <div className={itin.emptySegments}>
          <Plane size={20} />
          <span>No flight segments added yet</span>
        </div>
      </div>
    );
  }

  const isRT = String(flightType || "").toUpperCase() === "RT";
  const grouped = legs.length > 1;
  const showLegLabels = grouped || !!forcedDirection;

  return (
    <div className={itin.flightSegments}>
      <div className={grouped ? itin.segGroupCard : itin.segCard}>
        {legs.map((leg, i) => {
          const depCode = (leg.departure || leg.departureAirport || "").toUpperCase();
          const arrCode = (leg.arrival || leg.arrivalAirport || "").toUpperCase();
          const depAirportName = airportMap[depCode] || leg.departureName || leg.departureAirportName || "";
          const arrAirportName = airportMap[arrCode] || leg.arrivalName || leg.arrivalAirportName || "";
          const depTerminal = leg.departureTerminal || "";
          const arrTerminal = leg.arrivalTerminal || "";
          const depDate = leg.date || leg.departureDate || "";
          const depTime = leg.time || leg.departureTime || "";
          const arrDate = leg.arrivalDate || depDate;
          const arrTime = leg.arrivalTime || "";
          const fCode = `${leg.airlineCode || ""}${leg.flightNumber || ""}`;
          const airlineName = leg.airlineName || "";
          const legDirection = leg.direction || "";
          const dirPnrFallback = (forcedDirection || legDirection) === "Inbound" ? product.inboundPnr : product.outboundPnr;
          // Trimmed: a pasted code can carry a leading space, and this value is
          // what the copy button puts on the clipboard.
          const pnr = (leg.pnr || dirPnrFallback || product.airlinePnr || "").trim().toUpperCase();
          const cabin = leg.cabinClass || "";
          const fareLabel = leg.fareClassName || leg.fareClass || "";
          // A fully-cancelled flight (or a cancelled direction) forces its legs to
          // read "Cancelled" — the backend leaves each leg's flightStatus untouched
          // on a booking-level cancel, so mirror the Itinerary tab's override here.
          const effDir = forcedDirection || legDirection;
          const dirCancelled = (cancelledDirections || []).some(
            (dc) => String(dc).toLowerCase() === String(effDir).toLowerCase()
          );
          const status = (cancelledAll || dirCancelled)
            ? "Cancelled"
            : (leg.flightStatus || "Confirmed");
          const isLegCancelled = status === "Cancelled" || leg.legStatus === "Cancelled";
          const legLabel = forcedDirection
            ? forcedDirection
            : showLegLabels
              ? (legDirection || (isRT ? (i === 0 ? "Outbound" : "Return") : `Segment ${i + 1}`))
              : null;
          const duration = calcLegDuration(depTime, arrTime);
          const stops = leg.stops || 0;
          const isReturn = forcedDirection
            ? forcedDirection === "Inbound"
            : legDirection === "Inbound" || (isRT && i > 0);
          const checkedKg = legCheckedKg(leg);

          const chips = (
            <>
              {pnr && <div className={itin.segChip}><span className={itin.segChipLabel}>PNR</span><CopyableText text={pnr} className={itin.segChipCode} /></div>}
              <div className={itin.segChip}><span className={itin.segChipLabel}>Fare</span><span className={itin.segChipValue}>{fareLabel || "—"}</span></div>
              {checkedKg ? <div className={itin.segChip}><span className={itin.segChipLabel}>Baggage</span><span className={itin.segChipValue}>{checkedKg}kg</span></div> : null}
              <div className={`${itin.segStatusChip} ${itin[`segSt${status}`] || ""}`}>
                <span className={itin.segStatusDot} />
                {status}
              </div>
            </>
          );

          return (
            <div key={leg.id || i} className={grouped ? itin.segGroupLeg : undefined} style={isLegCancelled ? { opacity: 0.55 } : undefined}>
              {/* Divider between legs in a grouped card */}
              {i > 0 && grouped && <div className={itin.segGroupDivider} />}

              {/* Leg direction label */}
              {legLabel && (
                <div className={itin.segLegLabel}>
                  <span className={`${itin.segLegTag} ${isReturn ? itin.segLegRet : itin.segLegOut}`}>{legLabel}</span>
                  <div className={itin.segLegLine} />
                  <div className={itin.segLegChips}>{chips}</div>
                </div>
              )}

              <div className={itin.segRouteRow}>
                <div className={itin.segEndpoint}>
                  <div className={itin.segTopRow}>
                    <span className={itin.segTimeLarge}>{fmtLegTime(depTime)}</span>
                    <span className={itin.segIata}>{depCode || "—"}</span>
                  </div>
                  <div className={itin.segAirportName} title={depAirportName}>{depAirportName || depCode || "—"}</div>
                  <div className={itin.segDateText}>{formatDayDate(depDate)}{depTerminal ? ` · Terminal ${depTerminal}` : ""}</div>
                </div>

                <div className={itin.segRouteLine}>
                  <div className={itin.segFlightMeta}>
                    {airlineName && <span className={itin.segAirlineLabel}>{airlineName}</span>}
                    <span className={itin.segFlightCode}>{fCode || "—"}</span>
                    {cabin && <span className={itin.segCabinBadge}>{cabin}</span>}
                  </div>
                  <div className={itin.segLineTrack}>
                    <div className={itin.segDotStart} />
                    <div className={itin.segDash} />
                    <div className={itin.segPlaneWrapper}>
                      <Plane size={13} className={itin.segPlaneIcon} />
                    </div>
                    <div className={itin.segDash} />
                    <div className={itin.segDotEnd} />
                  </div>
                  <div className={itin.segRouteInfo}>
                    {duration && <span className={itin.segDuration}><Clock size={10} /> {duration}</span>}
                    <span className={itin.segStopsLabel}>{stops === 0 ? "Direct" : `${stops} stop${stops > 1 ? "s" : ""}`}</span>
                  </div>
                </div>

                <div className={`${itin.segEndpoint} ${itin.segEndpointRight}`}>
                  <div className={itin.segTopRow}>
                    <span className={itin.segTimeLarge}>{fmtLegTime(arrTime)}</span>
                    <span className={itin.segIata}>{arrCode || "—"}</span>
                  </div>
                  <div className={itin.segAirportName} title={arrAirportName}>{arrAirportName || arrCode || "—"}</div>
                  <div className={itin.segDateText}>{formatDayDate(arrDate)}{arrTerminal ? ` · Terminal ${arrTerminal}` : ""}</div>
                </div>
              </div>

              {/* Single-leg footer (only for OW / single segment in FULL mode) */}
              {!showLegLabels && (
                <div className={itin.segFooter}>
                  <div className={itin.segFooterChips}>{chips}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FlightPricingGroup = ({ product, travelers, assignments, pricing, onChange, onCopyToAll, onAutoFillTickets, onResetPricing, vatMeta, vatLoading, canc, onSavePricing, savingPricing, airportMap, flightSSRItems = [], travelerMods = [], hasClientPayments = false }) => {
  const isSplit = product.flightPricingMode === "SPLIT";
  // Both directions expanded by default; each can be collapsed independently.
  const [dirExpanded, setDirExpanded] = useState({ Outbound: true, Inbound: true });
  const toggleDir = (dir) => setDirExpanded((prev) => ({ ...prev, [dir]: !prev[dir] }));
  const [showBreakdown, setShowBreakdown] = useState(false);

  const legs = product.legs || [];
  const flightType = product.flightType || "OW";
  let outboundLegs = legs.filter((l) => (l.direction || "Outbound") !== "Inbound");
  let inboundLegs = legs.filter((l) => l.direction === "Inbound");
  // RT flights whose legs carry no direction info: first half out, second half back
  // (display only — pricing keys stay bound to the Outbound / Inbound labels).
  if (isSplit && inboundLegs.length === 0 && String(flightType).toUpperCase() === "RT" && legs.length >= 2) {
    const half = Math.ceil(legs.length / 2);
    outboundLegs = legs.slice(0, half);
    inboundLegs = legs.slice(half);
  }
  const tis = [...new Set(assignments.filter((a) => a.productId === product.id).map((a) => a.travelerIndex))];

  const computeDirTotals = (dir) => tis.reduce((acc, ti) => {
    const key = dir ? `${ti}-${product.id}-${dir}` : `${ti}-${product.id}`;
    const p = pricing[key] || {};
    acc.netPrice += p.netPrice || 0;
    acc.tax += p.tax || 0;
    acc.vatTax += p.vatTax || 0;
    acc.commission += p.commission || 0;
    acc.segFee += p.segFee || 0;
    acc.clientPrice += p.clientPrice || 0;
    return acc;
  }, { netPrice: 0, tax: 0, vatTax: 0, commission: 0, segFee: 0, clientPrice: 0 });

  const outTotals = isSplit ? computeDirTotals("Outbound") : computeDirTotals(null);
  const inTotals = isSplit ? computeDirTotals("Inbound") : null;

  /* SSR services that belong to this flight — priced inside this card so the
     flight and its services read as one product. */
  const hasSSRs = flightSSRItems.length > 0;
  const ssrTotals = computeSSRTotals(flightSSRItems, pricing);
  const grandTotals = isSplit ? {
    netPrice: outTotals.netPrice + inTotals.netPrice,
    tax: outTotals.tax + inTotals.tax,
    vatTax: outTotals.vatTax + inTotals.vatTax,
    commission: outTotals.commission + inTotals.commission,
    segFee: outTotals.segFee + inTotals.segFee,
    clientPrice: outTotals.clientPrice + inTotals.clientPrice,
  } : outTotals;

  const travCancInfo = useMemo(() => {
    if (!travelerMods.length) return null;
    const cancelledTIds = new Set(travelerMods.map((m) => m.travelerId));
    const hasCancelled = tis.some((ti) => {
      const t = travelers[ti];
      return t && ((t.status === "Cancelled") || (t.id && cancelledTIds.has(t.id)));
    });
    if (!hasCancelled) return null;
    const totalFees = travelerMods.reduce((s, m) => s + m.supplierCost + m.agencyFee, 0);
    const totalRefund = travelerMods.reduce((s, m) => s + m.refundAmount, 0);
    const cancelledClientPrice = tis.reduce((s, ti) => {
      const t = travelers[ti];
      if (!t || (t.status !== "Cancelled" && !(t.id && cancelledTIds.has(t.id)))) return s;
      const computeForDir = (dir) => {
        const key = dir ? `${ti}-${product.id}-${dir}` : `${ti}-${product.id}`;
        return (pricing[key] || {}).clientPrice || 0;
      };
      return s + (isSplit ? computeForDir("Outbound") + computeForDir("Inbound") : computeForDir(null));
    }, 0);
    const activeClientPrice = grandTotals.clientPrice - cancelledClientPrice;
    return { cancelledClientPrice, activeClientPrice, totalFees, totalRefund, refundDue: totalRefund };
  }, [travelerMods, tis, travelers, pricing, product.id, isSplit, grandTotals.clientPrice]);

  const getSegInfo = (dirLegs) => {
    if (!dirLegs || dirLegs.length === 0) return null;
    const first = dirLegs[0];
    const last = dirLegs[dirLegs.length - 1];
    const dep = (first.time || "").slice(0, 5);
    const arr = (last.arrivalTime || "").slice(0, 5);
    const airports = dirLegs.map((l) => l.departure || "—").concat([last.arrival || "—"]);
    return {
      departure: first.departure || "—",
      arrival: last.arrival || first.arrival || "—",
      airports,
      vias: airports.slice(1, -1),
      route: airports.join(" → "),
      date: formatDateShort(first.date),
      depTime: dep,
      arrTime: arr,
      timeStr: dep && arr ? `${dep} – ${arr}` : dep || arr || "—",
      flightCode: [first.airlineCode, first.flightNumber].filter(Boolean).join("") || "—",
      airlineName: first.airlineName || "",
      airlineCode: first.airlineCode || "",
      cabinClass: first.cabinClass || "—",
      stops: dirLegs.length - 1,
    };
  };

  const renderCollapsedBar = (direction, dirLegs) => {
    const info = getSegInfo(dirLegs);
    if (!info) return null;
    const colLegPnr = dirLegs.map(l => l.pnr).find(Boolean) || "";
    const colOwnPnr = direction === "Outbound" ? product.outboundPnr : direction === "Inbound" ? product.inboundPnr : "";
    const colDirPnr = colLegPnr
      || colOwnPnr
      || product.airlinePnr
      || (isSplit ? "" : (product.outboundPnr || product.inboundPnr || product.supplierRef))
      || "";
    const stopsLabel = info.stops === 0 ? "Direct" : `${info.stops} Stop${info.stops > 1 ? "s" : ""}`;
    return (
      <div className={`${s.segBar} ${s.segBarCollapsed}`}>
        <div className={s.segBarLeft}>
          <span className={`${s.dirBadge} ${direction === "Outbound" ? s.dirBadgeOut : s.dirBadgeIn}`}>{direction}</span>
          <RouteLine airports={info.airports} />
          <span className={s.segMetaGroup}>
            {info.date !== "—" && <span className={s.segMeta}>{info.date}</span>}
            {info.timeStr !== "—" && <span className={s.segMeta}>{info.timeStr}</span>}
            <span className={`${s.segStops} ${info.stops === 0 ? s.segStopsDirect : ""}`}>{stopsLabel}</span>
          </span>
          <span className={s.segChips}>
            <span className={s.segChip}><span className={s.segChipKey}>Flight</span><CopyableText text={info.flightCode} /></span>
            {colDirPnr && <span className={s.segChip}><span className={s.segChipKey}>PNR</span><CopyableText text={colDirPnr} /></span>}
          </span>
        </div>
        <div className={s.segBarActions}>
          <button type="button" className={s.expandPricingBtn} onClick={() => toggleDir(direction)}>
            Expand {direction} Pricing
            <ChevronIcon expanded={false} />
          </button>
        </div>
      </div>
    );
  };

  const flightTypeLabel = flightTypeLabelOf(flightType);

  const headerPaxCounts = tis.reduce((a, ti) => {
    const c = PAX_TYPE_MAP[classifyTraveler(travelers[ti]?.dob)] || "ADT";
    a[c] = (a[c] || 0) + 1;
    return a;
  }, {});
  const headerPaxLabel = [
    headerPaxCounts.ADT ? `${headerPaxCounts.ADT} Adult${headerPaxCounts.ADT > 1 ? "s" : ""}` : null,
    headerPaxCounts.CHD ? `${headerPaxCounts.CHD} Child${headerPaxCounts.CHD > 1 ? "ren" : ""}` : null,
    headerPaxCounts.INF ? `${headerPaxCounts.INF} Infant${headerPaxCounts.INF > 1 ? "s" : ""}` : null,
  ].filter(Boolean).join(", ") || `${tis.length} Traveler${tis.length !== 1 ? "s" : ""}`;

  /* Itinerary-card header data — same derivations as the Itinerary tab */
  const firstLeg = legs[0] || {};
  const travelDateStr = firstLeg.date || firstLeg.departureDate ? formatLongDate(firstLeg.date || firstLeg.departureDate) : "";
  const flightSub = legs.length > 1 ? `${flightTypeLabel} · ${legs.length} segments` : (firstLeg.direction || "");
  const contextLabel = product.flightBookingMode === "flightonly"
    ? "Flight Only"
    : product.flightBookingMode === "flightwithin" ? "Flight within Travel Booking" : "—";
  const headerStatus = canc ? (canc.cancelledAll ? "Cancelled" : "Partially Cancelled") : (product.status || "Pending");

  return (
    <div className={`${s.groupCard} ${itin.uCardFlight}`}>
      {/* ── Header — identical structure to the Itinerary tab flight card ── */}
      <div className={itin.uHead}>
        <div className={itin.uHeadL}>
          <div className={`${itin.uIcon} ${itin.uIconFlight}`}><Plane size={20} /></div>
          <span className={itin.uType}>FLIGHT</span>
          <ItStatusPill status={headerStatus} />
        </div>
        <div className={itin.uHeadR}>
          {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
        </div>
      </div>

      {/* Summary bar: Product Summary (route arrows + date) | Supplier | Supplier Ref */}
      <div className={itin.uSummary}>
        <div className={itin.uSumCell}>
          <span className={itin.uSumLabel}>Product Summary</span>
          <span className={itin.uSumTitle}>
            {legs.length > 0 ? (
              <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                <FlightRoute legs={legs} flightType={flightType} />
                {travelDateStr ? <span style={{ color: "#6b7280", fontWeight: 500 }}>• {travelDateStr}</span> : null}
              </span>
            ) : "Flight"}
          </span>
          {flightSub ? <span className={itin.uSumSub}>{flightSub}</span> : null}
        </div>
        <div className={itin.uSumCell}>
          <span className={itin.uSumLabel}>Supplier</span>
          <span className={itin.uSumTitle}>{product.supplier || "—"}</span>
        </div>
        <div className={itin.uSumCell}>
          <span className={itin.uSumLabel}>Supplier Ref</span>
          <span className={itin.uSumTitle}>{product.supplierRef ? <CopyableText text={product.supplierRef} /> : "—"}</span>
        </div>
      </div>

      {/* Icon-stat strip: Travelers | Type | Pricing | Context | VAT Role */}
      <div className={itin.uStats}>
        {[
          { icon: <Users size={16} />, label: "Travelers", value: headerPaxLabel },
          { icon: <Tag size={16} />, label: "Type", value: flightTypeLabel || "—" },
          { icon: <Tag size={16} />, label: "Pricing", value: isSplit ? "Split Flight" : "Full Flight" },
          { icon: <FileText size={16} />, label: "Context", value: contextLabel },
          { icon: <Percent size={16} />, label: "VAT Role", value: product.supplierCommercialRole || "—" },
        ].map((st, i) => (
          <div key={i} className={itin.uStat}>
            <span className={itin.uStatIcon}>{st.icon}</span>
            <span className={itin.uStatTxt}>
              <span className={itin.uStatLabel}>{st.label}</span>
              <span className={itin.uStatValue} title={typeof st.value === "string" ? st.value : undefined}>{st.value || "—"}</span>
            </span>
          </div>
        ))}
      </div>

      <CancellationBanner canc={canc} />
      <CancellationOverview canc={canc} />

      <div className={`${s.cardBody} ${s.cardBodyStacked}`}>
        <div className={s.cardMain}>
          {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
          <div className={canc?.cancelledAll ? cs.cancelledTableWrap : undefined}>

      {/* ── Per-direction totals toggle (SPLIT only); the full summary lives in the rail ── */}
      {isSplit && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 20px 0" }}>
          <button type="button" className={s.showBreakdownBtn} onClick={() => setShowBreakdown(!showBreakdown)}>
            {showBreakdown ? "Hide" : "Show"} Per-Direction Totals
            <ChevronIcon expanded={showBreakdown} />
          </button>
        </div>
      )}

      {/* ── Totals Breakdown ── */}
      {isSplit && showBreakdown && (
        <div className={s.breakdownGrid}>
          <div className={s.breakdownRow}>
            <span className={s.breakdownLabel} />
            <span className={s.breakdownHead}>Net Price</span>
            <span className={s.breakdownHead}>Tax</span>
            <span className={s.breakdownHead}>VAT Tax</span>
            <span className={s.breakdownHead}>Commission</span>
            <span className={s.breakdownHead}>Seg. Fee</span>
            <span className={`${s.breakdownHead} ${s.breakdownHeadAccent}`}>Client Price</span>
          </div>
          <div className={s.breakdownRow}>
            <span className={s.breakdownLabel}>Outbound</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.netPrice)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.tax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.vatTax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.commission)}</span>
            <span className={s.breakdownVal}>{formatCurrency(outTotals.segFee)}</span>
            <span className={`${s.breakdownVal} ${s.breakdownValAccent}`}>{formatCurrency(outTotals.clientPrice)}</span>
          </div>
          <div className={s.breakdownRow}>
            <span className={s.breakdownLabel}>Inbound</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.netPrice)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.tax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.vatTax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.commission)}</span>
            <span className={s.breakdownVal}>{formatCurrency(inTotals.segFee)}</span>
            <span className={`${s.breakdownVal} ${s.breakdownValAccent}`}>{formatCurrency(inTotals.clientPrice)}</span>
          </div>
          <div className={`${s.breakdownRow} ${s.breakdownRowTotal}`}>
            <span className={s.breakdownLabel}>Grand Total</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.netPrice)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.tax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.vatTax)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.commission)}</span>
            <span className={s.breakdownVal}>{formatCurrency(grandTotals.segFee)}</span>
            <span className={`${s.breakdownVal} ${s.breakdownValAccent}`}>{formatCurrency(grandTotals.clientPrice)}</span>
          </div>
        </div>
      )}

      {/* ── Outbound/Inbound toggles (SPLIT only) — click to collapse/expand each ── */}
      {isSplit && (
        <div className={s.dirTabs}>
          <div className={s.dirTabsInner}>
            <button type="button" className={`${s.dirTab} ${dirExpanded.Outbound ? s.dirTabActive : ""}`} onClick={() => toggleDir("Outbound")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              Outbound
              <ChevronIcon expanded={dirExpanded.Outbound} />
            </button>
            <button type="button" className={`${s.dirTab} ${dirExpanded.Inbound ? s.dirTabActive : ""}`} onClick={() => toggleDir("Inbound")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
              Inbound
              <ChevronIcon expanded={dirExpanded.Inbound} />
            </button>
          </div>
        </div>
      )}

      {/* ── FULL mode: itinerary-style segments + single pricing table ── */}
      {!isSplit && (
        <>
          <FlightSegmentsItinerary legs={legs} flightType={flightType} product={product} airportMap={airportMap} cancelledAll={canc?.cancelledAll} cancelledDirections={canc?.cancelledDirections || []} />
          <BlockLabel icon={<PlaneGlyph />}>Passenger Pricing</BlockLabel>
          <FlightPricingTable
            travelers={travelers}
            assignments={assignments}
            pricing={pricing}
            productId={product.id}
            onChange={onChange}
            onCopyToAll={onCopyToAll}
            onAutoFillTickets={onAutoFillTickets}
            directionLabel={null}
            vatMeta={vatMeta}
            vatLoading={vatLoading}
            travelerMods={travelerMods}
          />
        </>
      )}

      {/* ── SPLIT mode: both directions, each independently expand/collapse ── */}
      {isSplit && ["Outbound", "Inbound"].map((dir) => {
        const dirLegs = dir === "Outbound" ? outboundLegs : inboundLegs;
        if (!dirLegs.length) return null;
        return (
          <Fragment key={dir}>
            {dirExpanded[dir] ? (
              <>
                <FlightSegmentsItinerary legs={dirLegs} flightType={flightType} product={product} airportMap={airportMap} forcedDirection={dir} cancelledAll={canc?.cancelledAll} cancelledDirections={canc?.cancelledDirections || []} />
                <BlockLabel icon={<PlaneGlyph />}>{dir} Pricing</BlockLabel>
                <FlightPricingTable
                  travelers={travelers}
                  assignments={assignments}
                  pricing={pricing}
                  productId={product.id}
                  onChange={onChange}
                  onCopyToAll={onCopyToAll}
                  onAutoFillTickets={onAutoFillTickets}
                  directionLabel={dir}
                  vatMeta={vatMeta}
                  vatLoading={vatLoading}
                  travelerMods={travelerMods}
                />
              </>
            ) : (
              renderCollapsedBar(dir, dirLegs)
            )}
          </Fragment>
        );
      })}

          </div>{/* /read-only wrap */}

          {(canc?.removedDirections || []).map((dir, i) => (
            <div key={i} style={{ padding: "12px 20px 4px" }}>
              <CancelledDirectionBlock dir={dir} />
            </div>
          ))}

          {/* SSR services belonging to this flight — same table as before, now
              living inside the flight card. Stays editable independently of a
              flight cancellation, exactly like the old standalone card. */}
          {hasSSRs && (
            <>
              <BlockLabel icon={<SSRIcon />}>
                SSR Services · {flightSSRItems.length} item{flightSSRItems.length !== 1 ? "s" : ""} · Per-traveler pricing
              </BlockLabel>
              <SSRPricingTable
                ssrItems={flightSSRItems}
                pricing={pricing}
                onChange={onChange}
                vatMeta={vatMeta}
                vatLoading={vatLoading}
                totalsLabel="SSR Total"
                travelerMods={travelerMods}
              />
            </>
          )}
        </div>{/* /cardMain */}

        <div className={s.flightFooterWrap}>
          <FlightFooterSummary
            columns={canc
              ? [
                  { label: "Original Price", value: canc.originalSelling },
                  { label: "Cancelled Amount", value: canc.cancelledValue, cls: s.ffNeg, minus: true },
                  { label: "Cancellation Cost", value: canc.totalFees, cls: s.ffFees },
                  { label: "Active Amount", value: canc.activeSelling, cls: s.ffPos },
                  { label: "Refund to Client", value: canc.refundDue, cls: s.ffRefund },
                ]
              : travCancInfo
              ? [
                  { label: "Original Price", value: grandTotals.clientPrice + ssrTotals.selling },
                  { label: "Cancelled Amount", value: travCancInfo.cancelledClientPrice, cls: s.ffNeg, minus: true },
                  { label: "Cancellation Cost", value: travCancInfo.totalFees, cls: s.ffFees },
                  { label: "Active Amount", value: travCancInfo.activeClientPrice + ssrTotals.selling, cls: s.ffPos },
                  { label: "Refund to Client", value: hasClientPayments ? travCancInfo.totalRefund : 0, cls: s.ffRefund },
                ]
              : [
                  ...(isSplit ? [
                    { label: "Outbound", value: outTotals.clientPrice },
                    { label: "Inbound", value: inTotals.clientPrice },
                  ] : []),
                  { label: "Net Price", value: grandTotals.netPrice },
                  { label: "Taxes", value: grandTotals.tax },
                  { label: "Commission", value: grandTotals.commission },
                  { label: "Seg. Fee", value: grandTotals.segFee },
                  { label: "VAT (incl.)", value: grandTotals.vatTax },
                  ...(hasSSRs ? [{ label: "SSR Services", value: ssrTotals.selling }] : []),
                ]}
            canc={canc || travCancInfo}
            finalLabel="Final Selling Price (Active)"
            finalValue={canc ? canc.activeSelling + (canc.totalFees || 0) : travCancInfo ? travCancInfo.activeClientPrice + travCancInfo.totalFees + ssrTotals.selling : grandTotals.clientPrice + ssrTotals.selling}
            finalSub={hasSSRs && !canc && !travCancInfo ? "incl. taxes, fees & SSR services" : "incl. all taxes and fees"}
          />
        </div>
      </div>{/* /cardBody */}
      <PricingSaveFooter onSave={onSavePricing} saving={savingPricing} />
    </div>
  );
};

/* ─── Generic Pricing Group ──────────────────────────────────────────────── */

const GenericPricingGroup = ({ product, travelers, assignments, pricing, onChange, onCopyToAll, onResetPricing, vatMeta, vatLoading, canc, onSavePricing, savingPricing, travelerMods = [] }) => {
  const tis = [...new Set(assignments.filter((a) => a.productId === product.id).map((a) => a.travelerIndex))];
  let selling = 0;
  if (tis.length) {
    tis.forEach((ti) => { selling += (pricing[`${ti}-${product.id}`] || {}).sellingPrice || 0; });
  } else {
    selling = (pricing[`single-${product.id}`] || {}).sellingPrice || 0;
  }
  const typeLabel = String(product.type || "Product").replace(/([a-z])([A-Z])/g, "$1 $2");

  return (
    <div className={s.groupCard}>
      <div className={s.groupHeader}>
        <div className={s.groupHeaderId}>
          <div className={`${s.groupIcon} ${ICON_BG[product.type] || s.groupIconGeneric}`}>{PRODUCT_ICON_MAP[product.type] || Icons.box}</div>
          <div className={s.groupHeaderTitle}>
            <div className={s.groupTitle}>{typeLabel} Pricing</div>
            {product.supplier && <div className={s.groupTitleMeta}>{product.supplier}</div>}
          </div>
        </div>
        <MetaColumns items={[
          { label: "Travellers", value: tis.length ? `${tis.length} Traveller${tis.length !== 1 ? "s" : ""}` : "Whole product", always: true },
          { label: "Pricing Mode", value: <span className={`${s.modeBadge} ${s.modeBadgeFull}`}>{tis.length ? "Per Traveller" : "Fixed Price"}</span>, always: true },
          product.supplierRef && { label: "Supplier Ref", value: <CopyableText text={product.supplierRef} /> },
        ]} />
        <div className={s.groupHeaderRight}>
          {canc && <CancellationStatusPill status={canc.status} />}
          {onResetPricing && !canc?.cancelledAll && <button type="button" className={s.resetPriceBtn} onClick={onResetPricing} title="Reset all prices to zero"><ResetPriceIcon /> Reset Price</button>}
        </div>
      </div>

      <CancellationBanner canc={canc} />
      <CancellationOverview canc={canc} />

      <div className={s.cardBody}>
        <div className={s.cardMain}>
          <BlockLabel>{typeLabel} Pricing</BlockLabel>
          {canc?.cancelledAll && <div className={cs.cancelledCaption}>Cancelled — original pricing retained for reference (read-only).</div>}
          <div className={canc?.cancelledAll ? cs.cancelledTableWrap : undefined}>
            <PricingTable travelers={travelers} assignments={assignments} pricing={pricing} productId={product.id} onChange={onChange} onCopyToAll={onCopyToAll} vatMeta={vatMeta} vatLoading={vatLoading} travelerMods={travelerMods} />
          </div>
        </div>

        <PricingRail
          selling={{ title: `${typeLabel} Selling Price`, lines: [], subtotal: { label: "Selling Subtotal", value: selling } }}
          canc={canc}
          finalLabel="Final Selling Price (Active)"
          finalValue={canc ? canc.activeSelling + (canc.totalFees || 0) : selling}
          finalSub="incl. all taxes and fees"
        />
      </div>
      <PricingSaveFooter onSave={onSavePricing} saving={savingPricing} />
    </div>
  );
};

/* ─── SSR Pricing Section ────────────────────────────────────────────────── */

/* Compact flight-route chip shown under an SSR label so the operator can see at a
   glance which flight (or specific segment) the service is priced against. Blue
   flight accent stands apart from the card's pink SSR theme. Renders nothing when
   the SSR has no resolvable flight route. */
const SSRRouteChip = ({ route, scope, flightLabel }) => {
  if (!route || route.length === 0) return null;
  return (
    <span
      className={s.ssrRouteChip}
      title={scope === "segment" ? "Flight segment this SSR applies to" : "Flight this SSR applies to"}
    >
      <span className={s.ssrRoutePlane}><PlaneGlyph /></span>
      <span className={s.ssrRouteAirports}>
        {route.map((a, i) => (
          <Fragment key={i}>
            {i > 0 && <span className={s.ssrRouteArrow}>→</span>}
            <span className={s.ssrRouteCode}>{a}</span>
          </Fragment>
        ))}
      </span>
      {flightLabel
        ? <span className={s.ssrRouteFlight}>{flightLabel}</span>
        : scope === "flight" && <span className={s.ssrRouteScope}>Full flight</span>}
    </span>
  );
};

/* Pricing key for an SSR line — shared by the table, totals and VAT flow. */
const getSSRKey = (ssr, idx) => `ssr-${ssr.itemId}-${ssr.travelerId ?? idx}`;

/* Quantity-weighted SSR totals (cost / markup / VAT / selling). */
const computeSSRTotals = (ssrItems, pricing) =>
  (ssrItems || []).reduce((acc, ssr, idx) => {
    const p = pricing[getSSRKey(ssr, idx)] || {};
    const qty = ssr.qty || 1;
    acc.cost    += (p.costPrice    || 0) * qty;
    acc.markup  += (p.markup       || 0) * qty;
    acc.vat     += (p.vatTax       || 0) * qty;
    acc.selling += (p.sellingPrice || 0) * qty;
    return acc;
  }, { cost: 0, markup: 0, vat: 0, selling: 0 });

const getSSRTravName = (ssr) => {
  const t = ssr.travelerInformation;
  if (t) {
    const n = [t.firstName, (t.lastName || "").toUpperCase()].filter(Boolean).join(" ");
    if (n) return n;
  }
  return ssr.travelerLabel || "—";
};
const getSSRTravDOB = (ssr) => ssr.travelerInformation?.dob ? formatDOB(ssr.travelerInformation.dob) : "—";
const getSSRTravGender = (ssr) => {
  const g = ssr.travelerInformation?.gender;
  if (!g) return "—";
  const u = g.toUpperCase();
  if (u === "MALE" || u === "M") return "Male";
  if (u === "FEMALE" || u === "F") return "Female";
  return g;
};
const getSSRTravType = (ssr) => ssr.travelerInformation?.dob ? (PAX_TYPE_MAP[classifyTraveler(ssr.travelerInformation.dob)] || "ADT") : "—";

/* The SSR pricing table itself (head + rows + totals row) — rendered both
   inside the flight card the SSRs belong to and in the standalone fallback
   card for SSRs whose flight is no longer on the booking. */
const SSRPricingTable = ({ ssrItems, pricing, onChange, vatMeta, vatLoading, totalsLabel = "Total", travelerMods = [] }) => {
  const cancelledTravelerIds = useMemo(() => {
    const set = new Set();
    travelerMods.forEach((m) => set.add(m.travelerId));
    return set;
  }, [travelerMods]);

  const isSSRCancelled = (ssr) => {
    if (!ssr.travelerId) return false;
    return cancelledTravelerIds.has(ssr.travelerId);
  };

  const activeSSRs = ssrItems.filter((ssr) => !isSSRCancelled(ssr));
  const cancelledSSRs = ssrItems.filter((ssr) => isSSRCancelled(ssr));
  const hasCancelledSSRs = cancelledSSRs.length > 0;

  const totals = computeSSRTotals(ssrItems, pricing);
  const activeTotals = hasCancelledSSRs ? computeSSRTotals(activeSSRs, pricing) : totals;

  const renderSSRRow = (ssr, idx, cancelled) => {
    const key = getSSRKey(ssr, idx);
    const p   = pricing[key] || { costPrice: 0, sellingPrice: 0, markup: 0, vatTax: 0 };
    return (
      <div key={`${ssr.itemId}-${ssr.travelerId ?? idx}`} className={`${s.ssrRow} ${cancelled ? s.flightRowCancelled : ""}`}>
        <div className={s.ssrCellLabel}>
          <div className={s.ssrLabelStack}>
            <span className={s.ssrLabelText}>{ssr.label}</span>
            <SSRRouteChip
              route={ssr.flightRoute}
              scope={ssr.routeScope}
              flightLabel={ssr.flightLabel}
            />
          </div>
        </div>
        <div className={s.fCell}><span className={s.ssrCodeBadge}>{ssr.code}</span></div>
        <div className={s.fCell}><span className={`${s.cellType} ${TYPE_CLS[getSSRTravType(ssr)] || s.typeUnknown}`}>{getSSRTravType(ssr)}</span></div>
        <div className={`${s.fCell} ${s.fCellName}`}>
          <span className={s.travName}>
            {getSSRTravName(ssr)}
            {cancelled && <span className={s.travCancelledBadge}>Cancelled</span>}
          </span>
        </div>
        <div className={s.fCell}><span className={s.genderPill}>{getSSRTravGender(ssr)}</span></div>
        <div className={s.fCell}><span className={s.dobText}>{getSSRTravDOB(ssr)}</span></div>
        <div className={s.fCell} style={{ justifyContent: "center" }}><span className={s.ssrQtyBadge}>×{ssr.qty || 1}</span></div>
        <div className={s.fCell}>
          {cancelled
            ? <span className={s.cancelledStrike}>{formatCurrency(p.costPrice || 0)}</span>
            : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.costPrice ?? ""} onChange={(v) => onChange(key, "costPrice", v)} />
          }
        </div>
        <div className={s.fCell}>
          {cancelled
            ? <span className={s.cancelledStrike}>{formatCurrency(p.markup || 0)}</span>
            : <NumberInput className={`${s.fInput} ${s.numInput}`} value={p.markup ?? ""} onChange={(v) => onChange(key, "markup", v)} />
          }
        </div>
        <div className={s.fCell}>
          {cancelled
            ? <span className={s.cancelledStrike}>{formatCurrency(p.vatTax || 0)}</span>
            : <VATDisplay value={p.vatTax} meta={vatMeta?.[key]} loading={vatLoading?.[key]} />
          }
        </div>
        <div className={s.fCell}>
          <span className={`${s.clientPriceVal} ${cancelled ? s.cancelledStrike : ""}`}>{formatCurrency((p.sellingPrice || 0) * (ssr.qty || 1))}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={s.ssrTable}>
      <div className={s.ssrTableHead}>
        <span className={s.fth}>SSR Item</span>
        <span className={s.fth}>Code</span>
        <span className={s.fth}>Type</span>
        <span className={s.fth}>Traveler</span>
        <span className={s.fth}>Gender</span>
        <span className={s.fth}>DOB</span>
        <span className={s.fth} style={{ textAlign: "center" }}>Qty</span>
        <span className={`${s.fth} ${s.fthRight}`}>Cost Price</span>
        <span className={`${s.fth} ${s.fthRight}`}>Markup</span>
        <span className={`${s.fth} ${s.fthRight}`}>VAT</span>
        <span className={`${s.fth} ${s.fthRight} ${s.fthAccent}`}>Selling Price</span>
      </div>

      <div className={s.ssrTableBody}>
        {activeSSRs.map((ssr, idx) => renderSSRRow(ssr, idx, false))}
        {cancelledSSRs.length > 0 && (
          <>
            <div className={s.cancelledDivider}>
              <XCircle size={13} />
              <span>Cancelled Traveler SSR{cancelledSSRs.length > 1 ? "s" : ""}</span>
            </div>
            {cancelledSSRs.map((ssr, idx) => renderSSRRow(ssr, idx, true))}
          </>
        )}
      </div>

      {hasCancelledSSRs ? (
        <>
          <div className={`${s.ssrTotalsRow} ${s.flightTotalsRowCancelled}`}>
            <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 8" }}>Cancelled</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.cost - activeTotals.cost)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.markup - activeTotals.markup)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.vat - activeTotals.vat)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalValCancelled}`}>{formatCurrency(totals.selling - activeTotals.selling)}</span>
          </div>
          <div className={s.ssrTotalsRow}>
            <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 8" }}>Active {totalsLabel}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.cost)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.markup)}</span>
            <span className={s.flightTotalVal}>{formatCurrency(activeTotals.vat)}</span>
            <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(activeTotals.selling)}</span>
          </div>
        </>
      ) : (
        <div className={s.ssrTotalsRow}>
          <span className={s.flightTotalsLabel} style={{ gridColumn: "1 / 8" }}>{totalsLabel}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.cost)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.markup)}</span>
          <span className={s.flightTotalVal}>{formatCurrency(totals.vat)}</span>
          <span className={`${s.flightTotalVal} ${s.flightTotalAccent}`}>{formatCurrency(totals.selling)}</span>
        </div>
      )}
    </div>
  );
};

/* Standalone SSR card — fallback only, for SSR lines whose flight product is
   no longer on the booking. SSRs with a live flight render inside that
   flight's own pricing card instead. */
const SSRPricingSection = ({ ssrItems, pricing, onChange, vatMeta, vatLoading, onSavePricing, savingPricing, travelerMods = [] }) => {
  if (!ssrItems || ssrItems.length === 0) return null;

  const totals = computeSSRTotals(ssrItems, pricing);

  return (
    <div className={s.groupCard}>
      <div className={s.groupHeader}>
        <div className={`${s.groupIcon} ${s.groupIconSSR}`}><SSRIcon /></div>
        <div className={s.groupHeaderMain}>
          <div className={s.groupTitle}>SSR Services</div>
          <div className={s.groupSub}>
            {ssrItems.length} item{ssrItems.length !== 1 ? "s" : ""} · Per-traveler pricing
          </div>
        </div>
        {totals.selling > 0 && (
          <div className={s.groupHeaderRight}>
            <span className={s.ssrHeaderValue}>{formatCurrency(totals.selling)}</span>
          </div>
        )}
      </div>

      <SSRPricingTable ssrItems={ssrItems} pricing={pricing} onChange={onChange} vatMeta={vatMeta} vatLoading={vatLoading} travelerMods={travelerMods} />

      {/* Final Selling Price summary — same blue block products use */}
      <FlightFooterSummary
        columns={[
          { label: "Cost Price", value: totals.cost },
          { label: "Markup", value: totals.markup },
          { label: "VAT (incl.)", value: totals.vat },
        ]}
        finalLabel="Final Selling Price (Active)"
        finalValue={totals.selling}
        finalSub="incl. all taxes and fees"
      />
      <PricingSaveFooter onSave={onSavePricing} saving={savingPricing} />
    </div>
  );
};

/* ─── Main StepPricing ───────────────────────────────────────────────────── */

const BookingDetailPricing = ({ data, onChange, onCalculateVAT, hideSummary, onSavePricing, savingPricing }) => {
  const pricing  = data.pricing  || {};
  const ssrItems = data.ssrItems || [];
  const [vatMeta, setVatMeta] = useState({});
  const [vatLoading, setVatLoading] = useState({});
  const vatTimers = useRef({});
  const pricingRef = useRef(pricing);
  pricingRef.current = pricing;

  // Airport code → name lookup so flight cards show full airport names —
  // same source and shape as the Itinerary tab.
  const { data: airportsData } = useGetAirports({ limit: 20000, sortBy: "code", sortOrder: "ASC" });
  const airportMap = useMemo(() => {
    const map = {};
    (airportsData || []).forEach((a) => {
      if (a.code) map[a.code.toUpperCase()] = a.name || a.code;
    });
    return map;
  }, [airportsData]);

  const findProductForKey = useCallback((key) => {
    if (key.startsWith("hotelroom-")) {
      const match = key.match(/^hotelroom-(PRD-\d+)/);
      return match ? (data.products || []).find((p) => p.id === match[1]) : null;
    }
    if (key.startsWith("ssr-")) return null;
    const prdMatch = key.match(/(PRD-\d+)/);
    if (prdMatch) return (data.products || []).find((p) => p.id === prdMatch[1]);
    return null;
  }, [data.products]);

  const triggerVATCalculation = useCallback((key, updatedEntry) => {
    if (!onCalculateVAT) return;

    if (vatTimers.current[key]) clearTimeout(vatTimers.current[key]);

    vatTimers.current[key] = setTimeout(async () => {
      const isHotelRoom = key.startsWith("hotelroom-");
      const isSSR = key.startsWith("ssr-");

      let payload;
      if (isSSR) {
        const ssrItemId = key.split("-")[1];
        const ssr = ssrItems.find((s) => String(s.itemId) === ssrItemId);
        if (!ssr?._serverId) return;
        payload = {
          ssrId: ssr._serverId,
          markup: updatedEntry.markup || 0,
          cost_price: updatedEntry.costPrice || 0,
          selling_price: updatedEntry.sellingPrice || 0,
        };
      } else {
        const product = findProductForKey(key);
        if (!product?._serverId) return;

        if (product.type === "Flight") {
          const dirMatch = key.match(/-(Outbound|Inbound)$/);
          payload = {
            productId: product._serverId,
            net_price: updatedEntry.netPrice || 0,
            tax: updatedEntry.tax || 0,
            commission: updatedEntry.commission || 0,
            seg_fee: updatedEntry.segFee || 0,
            bound: dirMatch ? dirMatch[1].toLowerCase() : "outbound",
          };
        } else if (product.type === "Hotel") {
          payload = {
            productId: product._serverId,
            room_net_price: updatedEntry.netPrice || 0,
            markup: updatedEntry.markup || 0,
          };
        } else if (product.type === "Transfer") {
          payload = {
            productId: product._serverId,
            net_price: updatedEntry.costPrice || 0,
            commission: updatedEntry.markup || 0,
          };
        } else {
          // Generic products (Car Rental, Insurance, Visa, Package, Cruise, Incoming) —
          // VAT is computed server-side on the margin (markup) per product type.
          payload = {
            productId: product._serverId,
            net_price: updatedEntry.costPrice || 0,
            markup: updatedEntry.markup || 0,
          };
        }
      }

      setVatLoading((prev) => ({ ...prev, [key]: true }));
      try {
        const result = await onCalculateVAT(payload);
        if (result.success && result.data) {
          const vatAmount = result.data.vat_amount || 0;
          setVatMeta((prev) => ({ ...prev, [key]: result.data }));
          const latestPricing = pricingRef.current;
          const cur = latestPricing[key] || defaultPricingEntry();
          const upd = { ...cur, vatTax: vatAmount };
          if (isHotelRoom) {
            upd.sellingPrice = (upd.netPrice || 0) + (upd.markup || 0) + vatAmount;
          } else if (key.startsWith("ssr-")) {
            // SSR Fee is VAT-inclusive: VAT is embedded inside the markup (fee),
            // never added on top. Customer total = cost + fee.
            upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0);
          } else if (findProductForKey(key)?.type === "Flight") {
            upd.clientPrice = (upd.netPrice || 0) + (upd.tax || 0) + vatAmount + (upd.commission || 0) + (upd.segFee || 0);
            upd.sellingPrice = upd.clientPrice;
          } else {
            upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0) + vatAmount;
          }
          onChange("pricing", { ...latestPricing, [key]: upd });
        }
      } finally {
        setVatLoading((prev) => ({ ...prev, [key]: false }));
      }
    }, 600);
  }, [onCalculateVAT, data.products, ssrItems, findProductForKey, onChange]);

  const updatePricing = (key, field, value) => {
    const cur = pricing[key] || defaultPricingEntry();
    const upd = { ...cur };

    if (field === "ticketNumber") {
      upd.ticketNumber = value;
    } else {
      upd[field] = parseFloat(value) || 0;
    }

    const isHotelRoom = key.startsWith("hotelroom-");

    if (["netPrice", "tax", "commission", "segFee"].includes(field)) {
      if (isHotelRoom) {
        upd.sellingPrice = (upd.netPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
      } else {
        upd.clientPrice = (upd.netPrice || 0) + (upd.tax || 0) + (upd.vatTax || 0) + (upd.commission || 0) + (upd.segFee || 0);
        upd.sellingPrice = upd.clientPrice;
      }
    }

    if (field === "costPrice" || field === "markup") {
      const isSSR = key.startsWith("ssr-");
      upd.sellingPrice = isHotelRoom
        ? (upd.netPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0)
        : isSSR
          // SSR Fee is VAT-inclusive: VAT is embedded inside the fee, not added on top.
          ? (upd.costPrice || 0) + (upd.markup || 0)
          : (upd.costPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
    }

    onChange("pricing", { ...pricing, [key]: upd });

    const vatTriggerFields = isHotelRoom
      ? ["netPrice", "markup"]
      : key.startsWith("ssr-")
        ? ["costPrice", "markup"]
        : ["netPrice", "tax", "commission", "segFee", "costPrice", "markup"];
    if (vatTriggerFields.includes(field)) {
      triggerVATCalculation(key, upd);
    }
  };

  const resetProductPricing = useCallback((productId) => {
    const latest = pricingRef.current;
    const newPricing = { ...latest };
    Object.keys(newPricing).forEach((key) => {
      if (key.includes(productId)) {
        const cur = newPricing[key];
        const zeroed = {};
        Object.keys(cur).forEach((f) => {
          zeroed[f] = f === "ticketNumber" ? cur[f] : 0;
        });
        newPricing[key] = zeroed;
      }
    });
    onChange("pricing", newPricing);
  }, [onChange]);

  // Copy entered prices from one traveler to every other traveler in the same product.
  // Does a single batched state update so the closure sees fresh data, then schedules
  // VAT recalculation for each target so backend rates stay correct.
  const copyPricingToAll = useCallback((sourceKey, targetKeys, fields) => {
    const latest = pricingRef.current;
    const src = latest[sourceKey] || defaultPricingEntry();
    const newPricing = { ...latest };

    targetKeys.forEach((tk) => {
      if (tk === sourceKey) return;
      const cur = newPricing[tk] || defaultPricingEntry();
      const upd = { ...cur };
      fields.forEach((field) => { upd[field] = src[field] || 0; });

      // Recompute the derived price using the same rules as updatePricing.
      const isHotelRoom = tk.startsWith("hotelroom-");
      const isSSR = tk.startsWith("ssr-");
      if (isHotelRoom) {
        upd.sellingPrice = (upd.netPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
      } else if (isSSR) {
        // SSR Fee is VAT-inclusive: VAT is embedded inside the fee, not added on top.
        upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0);
      } else if (findProductForKey(tk)?.type === "Flight") {
        upd.clientPrice = (upd.netPrice || 0) + (upd.tax || 0) + (upd.vatTax || 0) + (upd.commission || 0) + (upd.segFee || 0);
        upd.sellingPrice = upd.clientPrice;
      } else {
        upd.sellingPrice = (upd.costPrice || 0) + (upd.markup || 0) + (upd.vatTax || 0);
      }

      newPricing[tk] = upd;
    });

    onChange("pricing", newPricing);

    // Trigger VAT recalc for every target so backend rates re-evaluate.
    targetKeys.forEach((tk) => {
      if (tk === sourceKey) return;
      triggerVATCalculation(tk, newPricing[tk]);
    });
  }, [onChange, findProductForKey, triggerVATCalculation]);

  const autoFillTickets = (productId, directionLabel, masterTicket, travelerIndices) => {
    const updated = { ...pricing };
    travelerIndices.forEach((ti, idx) => {
      const key = directionLabel ? `${ti}-${productId}-${directionLabel}` : `${ti}-${productId}`;
      const cur = updated[key] || defaultPricingEntry();
      updated[key] = { ...cur, ticketNumber: idx === 0 ? masterTicket : incrementTicket(masterTicket, idx) };
    });
    onChange("pricing", updated);
  };

  /* Stable key: re-run ticket pre-fill when ticket fields OR assignments change */
  const flightTicketsKey = useMemo(
    () => JSON.stringify({
      tickets: (data.products || [])
        .filter((p) => p.type === "Flight")
        .map((p) => ({
          id: p.id,
          mode: p.flightPricingMode,
          t: p.ticketNumber || "",
          o: p.outboundTicket || "",
          i: p.inboundTicket || "",
        })),
      assigns: (data.assignments || []).map((a) => `${a.productId}:${a.travelerIndex}`),
    }),
    [data.products, data.assignments]
  );

  /* Pre-fill flight ticket numbers — supports full + split pricing. Only fills empty slots. */
  useEffect(() => {
    const flightProducts = (data.products || []).filter(
      (p) => p.type === "Flight" &&
        (p.ticketNumber || p.outboundTicket || p.inboundTicket)
    );
    if (flightProducts.length === 0) return;

    const updated = { ...(data.pricing || {}) };
    let changed = false;

    flightProducts.forEach((product) => {
      const isSplit = product.flightPricingMode === "SPLIT";
      const tis = [
        ...new Set(
          (data.assignments || [])
            .filter((a) => a.productId === product.id)
            .map((a) => a.travelerIndex)
        ),
      ];
      if (tis.length === 0) return;

      const directions = isSplit ? ["Outbound", "Inbound"] : [null];
      directions.forEach((dir) => {
        const masterTicket = isSplit
          ? (dir === "Outbound" ? product.outboundTicket : product.inboundTicket)
          : product.ticketNumber;
        if (!masterTicket) return;

        tis.forEach((ti, idx) => {
          const key = dir
            ? `${ti}-${product.id}-${dir}`
            : `${ti}-${product.id}`;
          const cur = updated[key] || defaultPricingEntry();
          if (!cur.ticketNumber) {
            const ticket = idx === 0 ? masterTicket : incrementTicket(masterTicket, idx);
            updated[key] = { ...cur, ticketNumber: ticket };
            changed = true;
          }
        });
      });
    });

    if (changed) onChange("pricing", updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightTicketsKey]);

  /* Summary totals — count ONLY pricing entries that still map to a current
     product or SSR. Removing a product/SSR leaves its pricing entries behind in
     `data.pricing`; without this filter those orphans keep inflating the totals
     ("financial details of previous booking still there"). */
  const livePricedProductIds = new Set((data.products || []).map((p) => p.id));
  const liveSsrItemIds = new Set((data.ssrItems || []).map((sx) => String(sx.itemId)));
  const isLivePricingKey = (key) => {
    if (key.startsWith("ssr-")) return liveSsrItemIds.has(key.split("-")[1]);
    const m = key.match(/PRD-\d+/);
    return m ? livePricedProductIds.has(m[0]) : false;
  };
  /* Group SSR lines under the flight product they belong to so they render
     inside that flight's pricing card. SSRs whose flight is no longer on the
     booking fall back to the standalone SSR card — no line ever disappears. */
  const flightProductIds = new Set((data.products || []).filter((p) => p.type === "Flight").map((p) => p.id));
  const ssrByFlight = {};
  const orphanSSRItems = [];
  ssrItems.forEach((sx) => {
    if (sx.flightProductId && flightProductIds.has(sx.flightProductId)) {
      (ssrByFlight[sx.flightProductId] = ssrByFlight[sx.flightProductId] || []).push(sx);
    } else {
      orphanSSRItems.push(sx);
    }
  });

  const allValues    = Object.entries(pricing).filter(([k]) => isLivePricingKey(k)).map(([, v]) => v);
  const totalSelling = allValues.reduce((acc, p) => acc + (p.sellingPrice || p.clientPrice || 0), 0);
  const totalCost    = allValues.reduce((acc, p) => acc + (p.costPrice || ((p.netPrice || 0) + (p.tax || 0))), 0);
  const totalMargin  = totalSelling - totalCost;
  const adjustmentsTotal = (data.adjustments || []).reduce((sum, a) => {
    // Case-insensitive on mode/type so manual ({mode:'Fixed',type:'Surcharge'}) and
    // online ({mode:'fixed',type:'fee'}) adjustments compute identically.
    const val = String(a.mode || "").toLowerCase() === "percentage" ? (totalSelling * (a.value || 0)) / 100 : (a.value || 0);
    return sum + (String(a.type || "").toLowerCase() === "discount" ? -val : val);
  }, 0);
  const grandTotal = (data.bookingGrandTotal > 0) ? data.bookingGrandTotal : totalSelling + adjustmentsTotal;
  const marginPct  = totalSelling > 0 ? ((totalMargin / totalSelling) * 100).toFixed(1) : "0.0";

  const hotelProducts = (data.products || []).filter((p) => p.type === "Hotel");
  const hasHotels = hotelProducts.length > 0;
  const transferProducts = (data.products || []).filter((p) => p.type === "Transfer");
  const hasTransfers = transferProducts.length > 0;

  const hasFlightsOrGeneric = (data.products || []).some((p) => p.type !== "Hotel" && p.type !== "Transfer");
  if ((data.assignments || []).length === 0 && ssrItems.length === 0 && !hasHotels && !hasTransfers && !hasFlightsOrGeneric) return (
    <div className={styles.stepContent}>
      <div className={s.emptyWrap}>
        <div className={s.emptyIcon}>{Icons.wallet}</div>
        <div className={s.emptyTitle}>No assignments to price</div>
        <div className={s.emptyDesc}>Assign travelers to products first, then set pricing per traveler per product.</div>
      </div>
    </div>
  );

  /* Per-traveler groupings (exclude hotels and transfers — those render as fixed-price cards) */
  const grouped = {};
  (data.assignments || []).forEach((a) => {
    const prod = (data.products || []).find((p) => p.id === a.productId);
    if (!prod || prod.type === "Hotel" || prod.type === "Transfer") return;
    if (!grouped[a.productId]) grouped[a.productId] = [];
    if (!grouped[a.productId].includes(a.travelerIndex)) grouped[a.productId].push(a.travelerIndex);
  });
  // Also show Flight/Generic products that have no assignments yet (e.g. copied products)
  (data.products || []).forEach((prod) => {
    if (prod.type === "Hotel" || prod.type === "Transfer") return;
    if (!grouped[prod.id]) grouped[prod.id] = [];
  });

  const hasPackage = (data.products || []).some((p) => p.type === "Package");
  const PRODUCT_ORDER = hasPackage
    ? ["Package", "Transfer", "Insurance", "CarRental", "Visa", "Cruise", "Incoming", "Flight", "Hotel"]
    : ["Flight", "Hotel", "Transfer", "Insurance", "CarRental", "Visa", "Cruise", "Incoming", "Package"];
  const productOrder = (type) => { const i = PRODUCT_ORDER.indexOf(type); return i >= 0 ? i : 99; };

  return (
    <div className={styles.stepContent}>
      {!hideSummary && (
        <>
          {/* ── Summary Tiles ── */}
          <div className={s.summaryBar}>
            <div className={s.tile}>
              <span className={s.tileLabel}>Total Selling</span>
              <span className={s.tileValue}>{formatCurrency(totalSelling)}</span>
            </div>
            <div className={s.tile}>
              <span className={s.tileLabel}>Total Cost</span>
              <span className={s.tileValue}>{formatCurrency(totalCost)}</span>
            </div>
            <div className={s.tile}>
              <div className={s.tileLabelRow}>
                <span className={s.tileLabel}>Margin</span>
                <span className={`${s.tilePercent} ${totalMargin >= 0 ? s.tilePercentPos : s.tilePercentNeg}`}>
                  {totalMargin >= 0 ? <TrendUpIcon /> : <TrendDownIcon />}
                  {marginPct}%
                </span>
              </div>
              <span className={`${s.tileValue} ${totalMargin >= 0 ? s.tileValuePositive : s.tileValueNegative}`}>{formatCurrency(totalMargin)}</span>
            </div>
            <div className={`${s.tile} ${s.tileAccent}`}>
              <span className={s.tileLabel}>Grand Total</span>
              <span className={`${s.tileValue} ${s.tileValueAccent}`}>{formatCurrency(grandTotal)}</span>
            </div>
          </div>

          {adjustmentsTotal !== 0 && (
            <div className={s.infoStrip}>
              <InfoIcon />
              Adjustments: {formatCurrency(adjustmentsTotal)} · Outstanding: {formatCurrency(grandTotal)}
            </div>
          )}
        </>
      )}

      {(() => {
        const allSorted = (data.products || [])
          .slice()
          .sort((a, b) => productOrder(a.type) - productOrder(b.type));
        let accIdx = 0;
        const cancMap = data.cancellation || {};
        return allSorted.map((product) => {
          const canc = cancMap[product.id] || null;
          if (product.type === "Hotel") {
            accIdx += 1;
            return (
              <HotelPricingGroup
                key={product.id}
                product={product}
                travelers={data.travelers}
                assignments={data.assignments}
                pricing={pricing}
                onChange={updatePricing}
                onResetPricing={() => resetProductPricing(product.id)}
                accommodationIndex={accIdx}
                vatMeta={vatMeta}
                vatLoading={vatLoading}
                canc={canc}
                onSavePricing={onSavePricing}
                savingPricing={savingPricing}
              />
            );
          }
          if (product.type === "Transfer") {
            return (
              <TransferPricingGroup
                key={product.id}
                product={product}
                assignments={data.assignments}
                pricing={pricing}
                onChange={updatePricing}
                onResetPricing={() => resetProductPricing(product.id)}
                vatMeta={vatMeta}
                vatLoading={vatLoading}
                canc={canc}
                onSavePricing={onSavePricing}
                savingPricing={savingPricing}
              />
            );
          }
          if (product.type === "Flight") {
            return <FlightPricingGroup key={product.id} product={product} travelers={data.travelers} assignments={data.assignments} pricing={pricing} onChange={updatePricing} onCopyToAll={copyPricingToAll} onAutoFillTickets={autoFillTickets} onResetPricing={() => resetProductPricing(product.id)} vatMeta={vatMeta} vatLoading={vatLoading} canc={canc} onSavePricing={onSavePricing} savingPricing={savingPricing} airportMap={airportMap} flightSSRItems={ssrByFlight[product.id] || []} travelerMods={data.travelerMods} hasClientPayments={!!data.hasClientPayments} />;
          }
          return <GenericPricingGroup key={product.id} product={product} travelers={data.travelers} assignments={data.assignments} pricing={pricing} onChange={updatePricing} onCopyToAll={copyPricingToAll} onResetPricing={() => resetProductPricing(product.id)} vatMeta={vatMeta} vatLoading={vatLoading} canc={canc} onSavePricing={onSavePricing} savingPricing={savingPricing} travelerMods={data.travelerMods} />;
        });
      })()}

      <SSRPricingSection ssrItems={orphanSSRItems} pricing={pricing} onChange={updatePricing} vatMeta={vatMeta} vatLoading={vatLoading} onSavePricing={onSavePricing} savingPricing={savingPricing} travelerMods={data.travelerMods} />

      {data.saleType === "PACKAGE_TRAVEL" && (
        <div className={s.infoStrip}>
          <InfoIcon />
          Package Travel VAT: Margin-based VAT will be calculated automatically based on the total margin.
        </div>
      )}
    </div>
  );
};

export default BookingDetailPricing;